import React, { useState } from 'react';
import { Check, ChevronRight, ChevronLeft, Camera, Heart, Cake, Briefcase, Layout, Sliders, Wand2, User, Calendar, BookOpen, Percent, FileText, Plus, Trash2, RefreshCw } from 'lucide-react';
import { Quote, MarkupType, SelectedAlbum, SelectedPPVendor } from '../../types';
import { ALL_PRODUCTS, getProductNames, getProducts, getMatchingSemiFixed, getProductByCode, Product, ServiceKey, TierKey } from '../../data/productsData';
import { FIXED_ALBUM_OPTIONS, NO_ALBUM_CODES, PAPER_OPTIONS, COVER_OPTIONS } from '../../data/albumData';
import { CONTRACTORS, PP_VENDORS, MARKUP_OPTIONS } from '../../data/masterData';
import { calculateMarkup, calculateAlbumCost, formatCurrency, generateQuoteNumber, generateInvoiceNumber, SERVICE_NAMES, loadFromStorage } from '../../utils/helpers';
import { fetchEnquiries, createEnquiry, getConnectionDebugInfo, debugFetchEnquiries } from '../../utils/lifecycleApi';

// Enquiry Tracker's service tabs use these exact display strings — must match
// exactly for a newly-created enquiry to land in the right tab.
const SERVICE_KEY_TO_ENQUIRY_TYPE: Record<string, string> = {
  pre_wedding: 'Pre-Wedding', wedding: 'Wedding', engagement: 'Engagement',
  birthday: 'Birthday', office_event: 'Office Event',
};

interface NewQuoteProps { user: any; initialData?: Quote; initialMode?: 'quick'|'link-enquiry'|'new-lifecycle'; prelinkedEnquiry?: any; onSave: (q: Quote) => void; onSaved: () => void; }

// ── Types ─────────────────────────────────────────────────────────
interface BookingPackage {
  id: string;
  product: Product;
  eventDates: { date: string; label: string }[];
  numDays: number;
  contractor: string;
  contractorRate: number;
  contractorRatePerDay: number;
  selectedDeliverables: { deliverable: string; rate: number; ratePerDay: number; qty?: number }[];
  packageType: string; // 'Fixed' | 'Semi-Fixed' | 'Fully Personalized'
  album?: SelectedAlbum;
  albumOptionIndex?: number;
  noAlbum?: boolean; // explicitly skipped — customer doesn't want an album for this package
  actualSheets?: number;
  fpCameraQuality?: string;
  fpPPQuality?: string;
  fpAlbumQuality?: string;
  addOns: { id: string; name: string; cost: number; qty: number }[];
  originalProductCode?: string;
}

// ── Constants ─────────────────────────────────────────────────────
const SERVICES = [
  { key: 'pre_wedding'  as ServiceKey, label: 'Pre Wedding',    sub: 'Visual Session',       icon: <Heart size={15} />,    tiers: ['Luxe','Elite'] as TierKey[] },
  { key: 'wedding'      as ServiceKey, label: 'Wedding',         sub: 'Visual Session',       icon: <Camera size={15} />,   tiers: ['Best','Luxe','Elite'] as TierKey[] },
  { key: 'engagement'   as ServiceKey, label: 'Engagement',      sub: '/ Anniversary Shoot', icon: <Heart size={15} />,    tiers: ['Best','Luxe','Elite'] as TierKey[] },
  { key: 'birthday'     as ServiceKey, label: 'Birthday',        sub: 'Shoot',                icon: <Cake size={15} />,     tiers: ['Best','Luxe','Elite'] as TierKey[] },
  { key: 'office_event' as ServiceKey, label: 'Office / Event',  sub: 'Any Related Event',   icon: <Briefcase size={15} />,tiers: ['Best','Luxe','Elite'] as TierKey[] },
];

const TIERS: { id: TierKey; badge: string; desc: string }[] = [
  { id:'Best',  badge:'badge-best',  desc:'Quality contractors, value pricing' },
  { id:'Luxe',  badge:'badge-luxe',  desc:'Premium contractors, elevated finish' },
  { id:'Elite', badge:'badge-elite', desc:'Top-tier, finest quality' },
];

const MARKUP_OPTIONS_LIST = [
  { value: 35 as MarkupType, label: '35%' },
  { value: 45 as MarkupType, label: '45%' },
  { value: 55 as MarkupType, label: '55%' },
];

// Contractors by tier
const CONTRACTORS_BY_TIER: Record<TierKey, string[]> = {
  Best:  ['Shubh Gunn Photographic'],
  Luxe:  ['Shubh Gunn Photographic', 'Uttam Rai', 'Gunabh', 'Faizul', 'Jitendra'],
  Elite: ['Uttam Rai', 'Gunabh', 'Faizul', 'Guru', 'Jitendra'],
};

// ── Helper: does a contractor's rate-entry category satisfy a product's
// required category? Some contractors split their tier offering across a
// dedicated "<Tier> (Photo+Video)" category PLUS a bare "<Tier>" bucket used
// for extra inclusions like Candid Photography (varies per contractor — see
// masterData.ts). A strict exact-match misses that bare bucket and silently
// drops deliverables the product is actually supposed to include. This
// matches the combo category and its bare-tier sibling, but never the
// Full Day / Half Day variants (out of scope — no half/full day concept
// in this wizard).
function matchesContractorCategory(contractorCat: string, productCat: string): boolean {
  if (contractorCat === productCat) return true;
  if (productCat.endsWith(' (Photo+Video)')) {
    const bareTier = productCat.replace(' (Photo+Video)', '');
    return contractorCat === bareTier;
  }
  return false;
}

// ── Helper: get rate for contractor + product ─────────────────────
function getContractorRate(contractor: string, product: Product): number {
  const svcMap: Record<ServiceKey, string> = {
    pre_wedding: 'pre_wedding', wedding: 'wedding', engagement: 'engagement',
    birthday: 'birthday', office_event: 'office_event',
  };
  // Find best matching rate
  const matches = CONTRACTORS.filter(c =>
    c.contractor === contractor && c.status === 'Active' &&
    (c.rates as any)[product.serviceKey] !== undefined
  );
  if (!matches.length) return 0;
  // Pick first rate (simplify — show base rate)
  const rate = (matches[0].rates as any)[product.serviceKey];
  return typeof rate === 'number' ? rate : 0;
}

// ── Quote Edit: reconstruct wizard state from a saved Quote ────────
// Saved quotes store one entry per package in `packageItems`. This rebuilds
// the richer in-wizard BookingPackage shape (product lookup, selectedDeliverables,
// FP quality, album option index, extra charges) so editing starts from
// exactly what was last saved rather than a blank wizard.
function buildPackagesFromQuote(quote: Quote): BookingPackage[] {
  return (quote.packageItems || []).map((pkg: any) => {
    const product = getProductByCode(pkg.productCode);
    const selectedDeliverables = (pkg.contractors || []).map((c: any) => ({
      deliverable: c.deliverable, rate: c.rate, ratePerDay: c.rate, qty: c.qty || 1,
    }));
    const contractorRate = selectedDeliverables.reduce((s: number, d: any) => s + d.rate * (d.qty || 1), 0);

    // If the exact Fixed album card index wasn't stored (older quotes), try to
    // recover it by matching the saved album's paper/cover codes.
    let albumOptionIndex = pkg.albumOptionIndex;
    if (albumOptionIndex === undefined && pkg.album && product) {
      const opts = FIXED_ALBUM_OPTIONS[product.code]?.options || [];
      const foundIdx = opts.findIndex(o => o.paperCode === pkg.album.paperCode && o.coverCode === pkg.album.coverCode);
      if (foundIdx >= 0) albumOptionIndex = foundIdx;
    }

    // Fallback stub if the product code no longer exists in the catalog
    // (e.g. catalog was edited/recovered since this quote was saved) — keeps
    // the package editable instead of crashing the wizard.
    const fallbackProduct: Product = {
      code: pkg.productCode, serviceKey: pkg.serviceType, serviceName: pkg.serviceName, tier: pkg.qualityTier,
      packageType: pkg.packageType, productName: pkg.productName, productNameShort: pkg.productName,
      hasAlbum: !!pkg.album, numAlbums: pkg.album?.numAlbums || 0, coverageList: '', deliverables: '',
      contractorCategory: '', isVideoOnly: false, isDestination: false, isTogetherness: false,
    } as Product;

    return {
      id: pkg.id || `pkg_${Date.now()}_${Math.random()}`,
      product: product || fallbackProduct,
      eventDates: pkg.eventDates || [],
      numDays: Math.max(1, (pkg.eventDates || []).filter((d: any) => d.date).length),
      contractor: pkg.contractors?.[0]?.contractor || '',
      contractorRate,
      contractorRatePerDay: contractorRate,
      selectedDeliverables,
      packageType: pkg.packageType || 'Fixed',
      album: pkg.album,
      albumOptionIndex,
      noAlbum: !!pkg.noAlbum,
      fpCameraQuality: pkg.fpCameraQuality || '',
      fpPPQuality: pkg.fpPPQuality || '',
      fpAlbumQuality: pkg.fpAlbumQuality || '',
      addOns: pkg.addOns || [],
    };
  });
}

function buildExtraChargesFromQuote(quote: Quote): Record<string, { mode: 'sheets' | 'flat'; value: number }> {
  const result: Record<string, { mode: 'sheets' | 'flat'; value: number }> = {};
  (quote.packageItems || []).forEach((pkg: any) => {
    if (pkg.extraCharge && pkg.extraCharge.value) result[pkg.id] = pkg.extraCharge;
  });
  return result;
}

export default function NewQuote({ user, initialData, initialMode, prelinkedEnquiry, onSave, onSaved }: NewQuoteProps) {
  // ── Wizard step ──────────────────────────────────────────────
  const [step, setStep] = useState(1);

  // ── Step 1: Package selection state ──────────────────────────
  const [packages, setPackages] = useState<BookingPackage[]>(() => initialData ? buildPackagesFromQuote(initialData) : []);
  const [showAddForm, setShowAddForm] = useState(!initialData);

  // Add-form sub-state
  // ── Mode selection (Step 0) ──────────────────────────────────────────
  const [quoteMode, setQuoteMode] = useState<'quick' | 'link-enquiry' | 'new-lifecycle' | null>(
    initialData ? 'quick' : (prelinkedEnquiry ? 'link-enquiry' : (initialMode || null))
  );
  // Set directly when arriving from Enquiry Tracker's "Build Quote" button —
  // skips the search screen entirely since the enquiry is already known.
  const [linkedEnquiry, setLinkedEnquiry] = useState<any>(prelinkedEnquiry || null);
  const [enquirySearch, setEnquirySearch] = useState('');
  const [enquiryList, setEnquiryList] = useState<any[]>([]);
  const [enquiryLoading, setEnquiryLoading] = useState(false);
  const [enquiryLoadError, setEnquiryLoadError] = useState('');
  const [debugInfo, setDebugInfo] = useState<any[]>([]);
  // Feature: Planner + New Customer now creates the Enquiry right after the
  // Customer step, before the rest of the quote wizard — matching the real
  // SOP order (Enquiry is Stage 1, happens before commercial terms exist)
  // and meaning a follow-up-able record exists even if the quote is
  // abandoned partway through, rather than only at the very end.
  const [autoEnquiry, setAutoEnquiry] = useState<{ id: number; ref_number: string } | null>(null);
  const [enquiryCreating, setEnquiryCreating] = useState(false);
  const [enquiryCreateError, setEnquiryCreateError] = useState('');
  // After the enquiry is created (new-lifecycle mode), pause here and ask:
  // build a new quote for it now, or attach a quote that was already built
  // separately? Covers the case where a quote exists standalone and this
  // enquiry is genuinely new — no need to re-build the same quote twice.
  const [postEnquiryStep, setPostEnquiryStep] = useState<'choose' | null>(null);
  const [showAttachExisting, setShowAttachExisting] = useState(false);
  const [attachSearch, setAttachSearch] = useState('');
  const [attachedQuote, setAttachedQuote] = useState<Quote | null>(null);

  const handleAttachExisting = (q: Quote) => {
    if (!autoEnquiry) return;
    try {
      const links = JSON.parse(localStorage.getItem('ss_quote_enquiry_links') || '{}');
      const serviceKey = packages[0]?.product.serviceKey;
      links[q.id] = {
        enquiryId: autoEnquiry.id, ref_number: autoEnquiry.ref_number,
        service_type: (serviceKey && SERVICE_KEY_TO_ENQUIRY_TYPE[serviceKey]) || 'Any Event',
        customer_name: customerName,
      };
      localStorage.setItem('ss_quote_enquiry_links', JSON.stringify(links));
    } catch {}
    setAttachedQuote(q);
  };

  const ENQ_TABS = ['All','Wedding','Pre-Wedding','Engagement','Anniversary','Birthday','Office Event','Any Event'];
  const [enqTab, setEnqTab] = useState('All');
  const [enqLoadedCount, setEnqLoadedCount] = useState(0);

  // Loads ALL services into one combined, searchable list — like Master
  // Enquiry View — but sequentially, one request at a time, rather than all
  // 7 at once. Combined view is much better for actually finding a customer
  // (no clicking through tabs); sequential loading is what's been reliable.
  const loadAllEnquiries = async () => {
    setEnquiryLoading(true);
    setEnquiryLoadError('');
    setEnqLoadedCount(0);
    const combined: any[] = [];
    let firstError = '';
    let firstErrorService = '';
    for (const s of ENQ_TABS.filter(t => t !== 'All')) {
      try {
        const list = await fetchEnquiries(s);
        combined.push(...list);
      } catch (e: any) {
        if (!firstError) { firstError = e?.message || String(e); firstErrorService = s; }
      }
      setEnqLoadedCount(c => c + 1);
    }
    setEnquiryList(combined.filter((e: any) => e.overall_status !== 'Lost'));
    if (firstError) {
      setEnquiryLoadError(firstError);
      setDebugInfo([await debugFetchEnquiries(firstErrorService)]);
    } else {
      setDebugInfo([]);
    }
    setEnquiryLoading(false);
  };

  const [draftService, setDraftService]       = useState<ServiceKey | null>(null);
  const [draftPackageType, setDraftPackageType] = useState<string>('');
  const [draftTier, setDraftTier]             = useState<TierKey | null>(null);
  const [draftProductName, setDraftProductName] = useState<string>('');

  // ── Step 2: Customer ─────────────────────────────────────────
  const [customerName,  setCustomerName]  = useState(initialData?.customerName || prelinkedEnquiry?.customer_name || '');
  const [customerPhone, setCustomerPhone] = useState(initialData?.customerPhone || prelinkedEnquiry?.contact_number || '');
  const [customerEmail, setCustomerEmail] = useState(initialData?.customerEmail || '');
  const [eventName,     setEventName]     = useState(initialData?.eventName || (prelinkedEnquiry ? `${prelinkedEnquiry.customer_name || ''} Event` : ''));

  // ── Step 3: Dates per package ─────────────────────────────────
  // stored inside packages[].eventDates

  // ── Step 4: Contractor per package ───────────────────────────
  // stored inside packages[].contractor

  // ── Step 5: Album per package ─────────────────────────────────
  // stored inside packages[].album

  // ── Step 6: Markup + Summary ──────────────────────────────────
  const [markupType, setMarkupType] = useState<MarkupType>(initialData?.markupType ?? 45);
  const [saved,      setSaved]      = useState(false);
  // FP quality selections per package {pkgId: {cameraQuality, ppQuality, albumQuality}}
  const [fpQuality, setFpQuality] = useState<Record<string, {camera: string; pp: string; album: string}>>(() => {
    if (!initialData) return {};
    const result: Record<string, {camera:string;pp:string;album:string}> = {};
    (initialData.packageItems || []).forEach((pkg: any) => {
      if (pkg.fpCameraQuality || pkg.fpPPQuality || pkg.fpAlbumQuality) {
        result[pkg.id] = { camera: pkg.fpCameraQuality || '', pp: pkg.fpPPQuality || '', album: pkg.fpAlbumQuality || '' };
      }
    });
    return result;
  });

  // Extra day charges per package (for 5+ day events)
  const [extraCharges, setExtraCharges] = useState<Record<string, { mode: 'sheets' | 'flat'; value: number }>>(() => initialData ? buildExtraChargesFromQuote(initialData) : {});
  // Semi-Fixed album state per package
  const [semiFixedAlbumState, setSemiFixedAlbumState] = useState<Record<string, any>>({});
  const [extraAlbumSheets, setExtraAlbumSheets] = useState<Record<string,number>>(() => {
    const r:Record<string,number>={};
    (initialData?.packageItems||[]).forEach((p:any)=>{if(p.extraAlbumSheets)r[p.id]=p.extraAlbumSheets;});
    return r;
  });
  const [albumSplitSets, setAlbumSplitSets] = useState<Record<string,number>>(() => {
    const r:Record<string,number>={};
    (initialData?.packageItems||[]).forEach((p:any)=>{if(p.albumSplitSets)r[p.id]=p.albumSplitSets;});
    return r;
  });
  const [albumAdditionalCopies, setAlbumAdditionalCopies] = useState<Record<string,number>>(() => {
    const r:Record<string,number>={};
    (initialData?.packageItems||[]).forEach((p:any)=>{if(p.albumAdditionalCopies)r[p.id]=p.albumAdditionalCopies;});
    return r;
  });
  const [albumDesignChargePerSheet, setAlbumDesignChargePerSheet] = useState<Record<string,number>>(() => {
    const r:Record<string,number>={};
    (initialData?.packageItems||[]).forEach((p:any)=>{if(p.albumDesignChargePerSheet)r[p.id]=p.albumDesignChargePerSheet;});
    return r;
  });
  const [selectedPPVendor, setSelectedPPVendor] = useState<Record<string,string>>(() => {
    const r:Record<string,string>={};
    (initialData?.packageItems||[]).forEach((p:any)=>{if(p.ppVendorName)r[p.id]=p.ppVendorName;});
    return r;
  });
  const [pkgPPVendors, setPkgPPVendors] = useState<Record<string,SelectedPPVendor[]>>(() => {
    const r:Record<string,SelectedPPVendor[]>={};
    (initialData?.packageItems||[]).forEach((p:any)=>{if(p.ppVendors?.length)r[p.id]=p.ppVendors;});
    return r;
  });
  const [isInstantDelivery, setIsInstantDelivery] = useState<Record<string,boolean>>({});
  const [photoEditCharge, setPhotoEditCharge] = useState<Record<string,{enabled:boolean,rate:number,qty:number}>>(() => {
    const r:Record<string,{enabled:boolean,rate:number,qty:number}>={};
    (initialData?.packageItems||[]).forEach((p:any)=>{if(p.photoEditCharge?.enabled)r[p.id]=p.photoEditCharge;});
    return r;
  });
  const [originalProductCode] = useState<Record<string,string>>({});

  // ── Derived ──────────────────────────────────────────────────
  const availableTiers = draftService
    ? SERVICES.find(s => s.key === draftService)?.tiers || []
    : [];

  const availableProductNames = draftService && draftTier && draftPackageType
    ? getProductNames(draftService, draftTier, draftPackageType)
    : [];

  const draftProducts = draftService && draftTier && draftProductName && draftPackageType
    ? getProducts(draftService, draftTier, draftProductName, draftPackageType)
    : [];

  // For products with album/no-album variants, pick the one with album by default
  const resolvedProduct = draftProducts.find(p => p.hasAlbum) || draftProducts[0];

  // ── Add package ───────────────────────────────────────────────
  const addPackage = () => {
    if (!resolvedProduct) return;
    const fpQ = fpQuality['__draft__'] || { camera: '', pp: '', album: '' };
    const pkg: BookingPackage = {
      id: `pkg_${Date.now()}`,
      product: resolvedProduct,
      eventDates: [],
      numDays: 1,
      contractor: '',
      contractorRate: 0,
      contractorRatePerDay: 0,
      selectedDeliverables: [],
      packageType: draftPackageType || 'Fixed',
      fpCameraQuality: fpQ.camera || '',
      fpPPQuality: fpQ.pp || '',
      fpAlbumQuality: fpQ.album || '',
      addOns: [],
    };
    setPackages(prev => [...prev, pkg]);
    setDraftService(null); setDraftTier(null); setDraftProductName('');
    setShowAddForm(false);
  };

  const removePackage = (id: string) => setPackages(prev => prev.filter(p => p.id !== id));

  // Switch Fixed → Semi-Fixed (carries all data, clears album, updates product code)
  const switchToSemiFixed = (id: string) => {
    setPackages(prev => prev.map(p => {
      if (p.id !== id) return p;
      const semiEquivList = getMatchingSemiFixed(p.product);
      const semiEquiv = semiEquivList && semiEquivList.length > 0 ? semiEquivList[0] : {
        ...p.product,
        packageType: 'Semi-Fixed',
        code: p.product.code.replace('-PKG-', '-SEMI-'),
      };
      return {
        ...p,
        product: semiEquiv as any,
        packageType: 'Semi-Fixed',
        album: undefined,
        albumOptionIndex: undefined,
      };
    }));
    // Stay on album step — now shows Semi-Fixed album selector
  };

  const updatePackage = (id: string, updates: Partial<BookingPackage>) => {
    setPackages(prev => prev.map(p => p.id === id ? { ...p, ...updates } : p));
  };

  // ── Total cost ────────────────────────────────────────────────
  const totalCost = packages.reduce((sum, pkg) => {
    const ppVendorCost=(pkgPPVendors[pkg.id]||[]).reduce((s:number,v:any)=>s+v.rate*(v.qty||1),0);
    const photoEdit=photoEditCharge[pkg.id];
    const photoEditCost=photoEdit?.enabled?(photoEdit.rate||50)*(photoEdit.qty||0):0;
    // ── Album cost: primary = catalog price always ──
    const albumCost = pkg.album?.totalAlbumCost || 0;
    const designRate = albumDesignChargePerSheet[pkg.id] || 0;
    const baseSheets = pkg.album?.sheets || 40;
    const extraSheetCount = extraAlbumSheets[pkg.id] || 0;
    const numAlbums = pkg.album?.numAlbums || 1;
    // Design charges (primary + extra sheets)
    const primaryDesignCharge = designRate > 0 ? baseSheets * designRate * numAlbums : 0;
    const extraSheetDesign = designRate > 0 ? extraSheetCount * designRate * numAlbums : 0;
    // Extra sheets paper cost (catalog rate)
    const extraSheetPaper = pkg.album ? extraSheetCount * pkg.album.paperRatePerSheet * numAlbums : 0;
    const extraSheetCost = extraSheetPaper;
    // Split sets (extra covers only)
    const splitSets = albumSplitSets[pkg.id] || 1;
    const extraCoverCost = pkg.album && splitSets > 1 ? (splitSets-1)*pkg.album.coverRate*numAlbums : 0;
    // Additional copies = full album total × copies
    // Full album total = catalog + primary design + extra paper + extra design
    const fullAlbumOneCopy = albumCost + primaryDesignCharge + extraSheetPaper + extraSheetDesign;
    const additionalCopies = albumAdditionalCopies[pkg.id] || 0;
    const additionalCopyCost = additionalCopies * fullAlbumOneCopy;
    const albumAdjTotal = primaryDesignCharge + extraSheetDesign + extraSheetPaper + extraCoverCost + additionalCopyCost;
    const totalAdjust = ppVendorCost + photoEditCost + albumAdjTotal;
    const liveDays = Math.max(1, pkg.eventDates.filter(d => d.date).length);
    const liveContractorRate = (pkg.selectedDeliverables || []).reduce((s, d) => {
      return s + (d.ratePerDay || d.rate) * liveDays * (d.qty || 1);
    }, 0);
    const addOnCost = (pkg.addOns || []).reduce((s, a) => s + (a.cost * (a.qty || 1)), 0);
    const ec = extraCharges[pkg.id];
    let extraCost = 0;
    if (ec && ec.value > 0 && pkg.album) {
      if (ec.mode === 'sheets') {
        extraCost = ec.value * pkg.album.paperRatePerSheet * (pkg.product.numAlbums || 1);
      } else {
        extraCost = ec.value;
      }
    }
    return sum + (liveContractorRate || pkg.contractorRate) + albumCost + addOnCost + extraCost + totalAdjust;
  }, 0);
  const { markupAmount, quotedAmount, markupPercent } = calculateMarkup(totalCost, markupType);

  // ── Save ──────────────────────────────────────────────────────
  const handleSave = () => {
    const quote: Quote = {
      id: initialData?.id || `q_${Date.now()}`,
      quoteNumber: initialData?.quoteNumber || generateQuoteNumber(),
      customerName, customerPhone, customerEmail,
      eventName,
      createdBy: initialData?.createdBy || user.name,
      createdAt: initialData?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      status: initialData?.status || 'draft',
      packageItems: packages.map(pkg => ({
        id: pkg.id,
        serviceType: pkg.product.serviceKey as any,
        serviceName: pkg.product.serviceName,
        qualityTier: pkg.product.tier as any,
        packageType: pkg.packageType as any,
        productCode: pkg.product.code,
        productName: pkg.product.productName,
        eventDates: pkg.eventDates,
        coverageTypes: [],
        contractors: (pkg.selectedDeliverables||[]).map(d => ({ contractor: pkg.contractor, deliverable: d.deliverable, rate: d.rate, qty: d.qty || 1 })),
        ppVendors: pkgPPVendors[pkg.id]||[],
        album: pkg.album,
        albumOptionIndex: pkg.albumOptionIndex,
        noAlbum: !!pkg.noAlbum,
        addOns: pkg.addOns || [],
        fpCameraQuality: pkg.fpCameraQuality,
        fpPPQuality: pkg.fpPPQuality,
        fpAlbumQuality: pkg.fpAlbumQuality,
        extraCharge: extraCharges[pkg.id],
        ppVendorName: selectedPPVendor[pkg.id]||undefined,
        extraAlbumSheets: (extraAlbumSheets[pkg.id]||0)||undefined,
        albumSplitSets: (albumSplitSets[pkg.id]||1)>1?albumSplitSets[pkg.id]:undefined,
        albumAdditionalCopies: (albumAdditionalCopies[pkg.id]||0)||undefined,
        albumDesignChargePerSheet: albumDesignChargePerSheet[pkg.id]||undefined,
        photoEditCharge: photoEditCharge[pkg.id]||undefined,
        subtotal: pkg.contractorRate + (pkg.album?.totalAlbumCost || 0)
          + (pkg.addOns||[]).reduce((s,a) => s + (a.cost * (a.qty||1)), 0)
          + (extraCharges[pkg.id]?.value && pkg.album
              ? (extraCharges[pkg.id].mode === 'sheets'
                  ? extraCharges[pkg.id].value * pkg.album.paperRatePerSheet * (pkg.product.numAlbums || 1)
                  : extraCharges[pkg.id].value)
              : 0),
      })),
      totalCost, markupType, markupAmount, quotedAmount,
    };
    onSave(quote);
    // Feature: the Enquiry was created back at the Customer step (see step 2)
    // — link it to this now-saved quote via the same mechanism Quote Quick
    // Look's freehand "Link to Existing Enquiry" uses, so Enquiry Tracker's
    // Create Booking modal auto-picks it up exactly like any other link.
    if (autoEnquiry) {
      try {
        const links = JSON.parse(localStorage.getItem('ss_quote_enquiry_links') || '{}');
        links[quote.id] = { enquiryId: autoEnquiry.id, ref_number: autoEnquiry.ref_number, service_type: packages[0]?.product.serviceKey ? SERVICE_KEY_TO_ENQUIRY_TYPE[packages[0].product.serviceKey] : '', customer_name: customerName };
        localStorage.setItem('ss_quote_enquiry_links', JSON.stringify(links));
      } catch {}
    }
    setSaved(true);
    setTimeout(() => { setSaved(false); onSaved(); }, 1500);
  };

  if (saved) {
    return (
      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:400, gap:16 }}>
        <div style={{ width:60, height:60, borderRadius:'50%', background:'var(--teal)', display:'flex', alignItems:'center', justifyContent:'center' }}>
          <Check size={28} color="white" />
        </div>
        <h2>{initialData ? 'Quote Updated!' : 'Quote Saved!'}</h2>
      </div>
    );
  }

  const STEPS = ['Service & Product','Customer','Event Dates','Contractor','PP Vendor','Mapping','Album','Add-ons','Summary'];
  const canNext1 = packages.length > 0 && !showAddForm;
  const canNext2 = customerName.trim() && customerPhone.trim() && eventName.trim();
  const canNext3 = packages.every(p => p.eventDates.length > 0 && p.eventDates[0].date);
  const canNext4 = packages.every(p => p.contractor);
  const canNext6 = packages.every(p => { const effHasAlbum=p.product.hasAlbum||p.packageType==='Semi-Fixed'||p.packageType==='Fully Personalized'; return !effHasAlbum||p.album||p.noAlbum; });

  return (
    <div style={{ padding:28, maxWidth:860 }}>
      <div style={{ marginBottom:24 }}>
        <h1>{initialData ? 'Edit Quote' : 'New Quote'}</h1>
        <p style={{ color:'var(--text-secondary)', fontSize:13, marginTop:4 }}>
          {initialData ? <>Editing <strong style={{ fontFamily:'monospace' }}>{initialData.quoteNumber}</strong> — saving will update this quote, not create a new one</> : 'Create a new customer quote'}
        </p>
      </div>

      {/* ── OPTION SELECTION — shown first, nothing else visible ── */}
      {quoteMode === null && (
        <div>
          <p style={{ color:'var(--text-secondary)', fontSize:13, marginBottom:28 }}>
            Choose how you want to proceed — this determines what steps are shown.
          </p>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16 }}>

            {/* Option 1: Quick Quote */}
            <div onClick={() => setQuoteMode('quick')}
              style={{ padding:28, borderRadius:12, border:'2px solid var(--border)', cursor:'pointer', background:'#fff', transition:'all 0.15s' }}
              onMouseEnter={e=>{(e.currentTarget as HTMLDivElement).style.borderColor='var(--gold)';(e.currentTarget as HTMLDivElement).style.boxShadow='0 4px 16px rgba(184,134,11,0.12)';}}
              onMouseLeave={e=>{(e.currentTarget as HTMLDivElement).style.borderColor='var(--border)';(e.currentTarget as HTMLDivElement).style.boxShadow='none';}}>
              <div style={{ fontSize:32, marginBottom:12 }}>⚡</div>
              <div style={{ fontWeight:700, fontSize:16, marginBottom:8, color:'var(--dark)' }}>Quick Quote</div>
              <div style={{ fontSize:13, color:'var(--text-secondary)', lineHeight:1.6, marginBottom:16 }}>
                For walk-in customers, rate checking, or reference purposes. Just the planner — no lifecycle tracking.
              </div>
              <div style={{ borderTop:'1px solid var(--border)', paddingTop:12, fontSize:12, color:'var(--text-muted)' }}>
                Quote saved → Done. Can add to lifecycle later if needed.
              </div>
            </div>

            {/* Option 2: Planner + Existing Enquiry */}
            <div onClick={async () => { setQuoteMode('link-enquiry'); await loadAllEnquiries(); }}
              style={{ padding:28, borderRadius:12, border:'2px solid var(--border)', cursor:'pointer', background:'#fff', transition:'all 0.15s' }}
              onMouseEnter={e=>{(e.currentTarget as HTMLDivElement).style.borderColor='var(--teal)';(e.currentTarget as HTMLDivElement).style.boxShadow='0 4px 16px rgba(56,178,172,0.12)';}}
              onMouseLeave={e=>{(e.currentTarget as HTMLDivElement).style.borderColor='var(--border)';(e.currentTarget as HTMLDivElement).style.boxShadow='none';}}>
              <div style={{ fontSize:32, marginBottom:12 }}>🔗</div>
              <div style={{ fontWeight:700, fontSize:16, marginBottom:8, color:'var(--dark)' }}>Planner + Existing Enquiry</div>
              <div style={{ fontSize:13, color:'var(--text-secondary)', lineHeight:1.6, marginBottom:16 }}>
                Customer is already in the Enquiry Tracker. Build their quote and link it — full lifecycle continues from where it left off.
              </div>
              <div style={{ borderTop:'1px solid var(--border)', paddingTop:12, fontSize:12, color:'var(--text-muted)' }}>
                Select enquiry → customer details auto-fill → quote linked → lifecycle tracked.
              </div>
            </div>

            {/* Option 3: Planner + New Customer */}
            <div onClick={() => setQuoteMode('new-lifecycle')}
              style={{ padding:28, borderRadius:12, border:'2px solid var(--border)', cursor:'pointer', background:'#fff', transition:'all 0.15s' }}
              onMouseEnter={e=>{(e.currentTarget as HTMLDivElement).style.borderColor='#7C3AED';(e.currentTarget as HTMLDivElement).style.boxShadow='0 4px 16px rgba(124,58,237,0.12)';}}
              onMouseLeave={e=>{(e.currentTarget as HTMLDivElement).style.borderColor='var(--border)';(e.currentTarget as HTMLDivElement).style.boxShadow='none';}}>
              <div style={{ fontSize:32, marginBottom:12 }}>🆕</div>
              <div style={{ fontWeight:700, fontSize:16, marginBottom:8, color:'var(--dark)' }}>Planner + New Customer</div>
              <div style={{ fontSize:13, color:'var(--text-secondary)', lineHeight:1.6, marginBottom:16 }}>
                Brand new customer — not in the system yet. The Enquiry gets created first, right after Customer Details — then you build the quote for it.
              </div>
              <div style={{ borderTop:'1px solid var(--border)', paddingTop:12, fontSize:12, color:'var(--text-muted)' }}>
                Customer details → enquiry created → quote built → lifecycle begins.
              </div>
            </div>

          </div>
        </div>
      )}

      {/* ── MODE 2: Enquiry Search — shown only when link-enquiry selected, no step bar ── */}
      {quoteMode === 'link-enquiry' && !linkedEnquiry && (
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => { setQuoteMode(null); setEnquiryList([]); setEnquiryLoadError(''); }}>← Back</button>
            <div>
              <h2 style={{ margin:0, fontSize:18 }}>Select Enquiry to Link</h2>
              <p style={{ margin:0, fontSize:12, color:'var(--text-muted)' }}>Pick the customer whose enquiry is already in the tracker</p>
            </div>
          </div>
          <input className="input" placeholder="🔍  Search by customer name or ref number..." value={enquirySearch}
            onChange={e => setEnquirySearch(e.target.value)} style={{ marginBottom:16, maxWidth:500 }} autoFocus />
          <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:16 }}>
            {ENQ_TABS.map(t => (
              <button key={t} onClick={() => setEnqTab(t)}
                style={{ padding:'6px 14px', borderRadius:100, fontSize:12.5, fontWeight: enqTab===t?700:500,
                  background: enqTab===t?'rgba(183,121,31,0.15)':'var(--bg-subtle)',
                  color: enqTab===t?'var(--gold)':'var(--text-secondary)',
                  border: enqTab===t?'1px solid rgba(183,121,31,0.3)':'1px solid transparent', cursor:'pointer' }}>
                {t}
              </button>
            ))}
          </div>
          {enquiryLoading && <div style={{ color:'var(--text-muted)', fontSize:13, padding:20 }}>Loading enquiries… ({enqLoadedCount}/7 services checked)</div>}
          {!enquiryLoading && enquiryLoadError && (
            <div style={{ padding:20, background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:8, marginBottom:16 }}>
              <div style={{ fontWeight:700, color:'#B91C1C', marginBottom:6 }}>⚠ Couldn't load enquiries — this is the actual technical reason:</div>
              <div style={{ fontFamily:'monospace', fontSize:13, color:'#7F1D1D', background:'#fff', padding:'8px 12px', borderRadius:6, wordBreak:'break-word' }}>{enquiryLoadError}</div>
              <div style={{ fontSize:12, color:'#7F1D1D', marginTop:8 }}>Screenshot this box and send it — this is the real error, not a guess.</div>
              <button className="btn btn-secondary btn-sm" style={{ marginTop:10, fontSize:12 }} onClick={() => loadAllEnquiries()}>🔄 Try Again</button>
            </div>
          )}
          {!enquiryLoading && !enquiryLoadError && enquiryList.length === 0 && (
            <div style={{ padding:32, textAlign:'center', color:'var(--text-muted)', background:'var(--bg-subtle)', borderRadius:8 }}>
              <div style={{ fontSize:24, marginBottom:8 }}>📋</div>
              <div style={{ fontWeight:600, marginBottom:4 }}>No enquiries found</div>
              <div style={{ fontSize:13 }}>Go to Enquiry Tracker to create one for this customer.</div>
              <button className="btn btn-secondary btn-sm" style={{ marginTop:14, fontSize:12 }} onClick={() => loadAllEnquiries()}>🔄 Try Again</button>
            </div>
          )}
          {!enquiryLoading && enquiryList.length === 0 && debugInfo.length > 0 && (
            <div style={{ marginTop:16, padding:14, background:'#111827', borderRadius:8, fontFamily:'monospace', fontSize:11.5 }}>
              <div style={{ color:'#9CA3AF', marginBottom:8 }}>DIAGNOSTIC — screenshot this box exactly as-is:</div>
              <div style={{ color:'#93C5FD', marginBottom:6 }}>Token: {(() => { const c = getConnectionDebugInfo(); return c.hasToken ? `present (${c.tokenPreview})` : 'MISSING — not connected'; })()}</div>
              {debugInfo.map((d: any, i: number) => (
                <div key={i} style={{ color: d.ok && d.count > 0 ? '#86EFAC' : d.ok ? '#FCD34D' : '#FCA5A5' }}>
                  {d.service}: HTTP {d.status} · {d.count} results {d.count <= 0 ? `· raw: ${d.raw}` : ''}
                </div>
              ))}
            </div>
          )}
          {!enquiryLoading && enquiryList.length > 0 && enquiryList.filter((e:any) =>
            (enqTab === 'All' || e.service_type === enqTab) &&
            (!enquirySearch ||
              e.customer_name?.toLowerCase().includes(enquirySearch.toLowerCase()) ||
              e.ref_number?.includes(enquirySearch))
          ).length === 0 && (
            <div style={{ padding:20, textAlign:'center', color:'var(--text-muted)', fontSize:13 }}>
              No matches in {enqTab === 'All' ? 'any service' : enqTab} for "{enquirySearch}" — try another tab or search term.
            </div>
          )}
          {!enquiryLoading && enquiryList.filter((e:any) =>
            (enqTab === 'All' || e.service_type === enqTab) &&
            (!enquirySearch ||
              e.customer_name?.toLowerCase().includes(enquirySearch.toLowerCase()) ||
              e.ref_number?.includes(enquirySearch))
          ).map((e:any) => (
            <div key={e.id} onClick={() => {
              setLinkedEnquiry(e);
              setCustomerName(e.customer_name || '');
              setCustomerPhone(e.contact_number || '');
              setEventName((e.customer_name || '') + ' Event');
            }} style={{ padding:'14px 18px', borderRadius:8, marginBottom:8, cursor:'pointer', border:'1.5px solid var(--border)', background:'#fff', display:'flex', alignItems:'center', justifyContent:'space-between' }}
              onMouseEnter={e2=>(e2.currentTarget as HTMLDivElement).style.borderColor='var(--teal)'}
              onMouseLeave={e2=>(e2.currentTarget as HTMLDivElement).style.borderColor='var(--border)'}>
              <div>
                <div style={{ fontWeight:600, fontSize:14 }}>{e.customer_name}</div>
                <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:2 }}>
                  {e.ref_number} &nbsp;·&nbsp; {e.service_type} &nbsp;·&nbsp; {e.event_dates || 'Date TBD'}
                  {e.suggested_package && <> &nbsp;·&nbsp; {e.suggested_package}</>}
                </div>
              </div>
              <span style={{ fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:100,
                background: e.overall_status==='Confirmed'?'#D1FAE5':e.overall_status==='Open'?'#EBF8FF':'#FFF7ED',
                color: e.overall_status==='Confirmed'?'#065F46':e.overall_status==='Open'?'#1E3A5F':'#92400E' }}>
                {e.overall_status}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* ── Active mode banners (shown above step bar when in wizard) ── */}
      {linkedEnquiry && quoteMode === 'link-enquiry' && (
        <div style={{ padding:'10px 16px', background:'rgba(56,178,172,0.08)', border:'1.5px solid var(--teal)', borderRadius:8, marginBottom:20, display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:600, fontSize:13, color:'var(--teal)' }}>🔗 Linked: {linkedEnquiry.ref_number} — {linkedEnquiry.customer_name}</div>
            <div style={{ fontSize:12, color:'var(--text-secondary)' }}>{linkedEnquiry.service_type} · {linkedEnquiry.event_dates || 'TBD'} · Customer details pre-filled from enquiry</div>
          </div>
          <button className="btn btn-ghost btn-sm" style={{ fontSize:11 }} onClick={async () => { setLinkedEnquiry(null); setQuoteMode('link-enquiry'); await loadAllEnquiries(); }}>Change</button>
        </div>
      )}
      {quoteMode === 'new-lifecycle' && (
        <div style={{ padding:'10px 16px', background:'#F3F0FF', border:'1.5px solid #C4B5FD', borderRadius:8, marginBottom:20, display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:600, fontSize:13, color:'#5B21B6' }}>🆕 New Customer — Full Lifecycle</div>
            <div style={{ fontSize:12, color:'var(--text-secondary)' }}>
              {autoEnquiry
                ? <>✓ Enquiry <strong>{autoEnquiry.ref_number}</strong> created — now build the quote for it.</>
                : 'Enquiry gets created as soon as you continue past Customer Details, before the rest of the quote.'}
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" style={{ fontSize:11 }} onClick={() => setQuoteMode(null)}>Change</button>
        </div>
      )}
      {quoteMode === 'quick' && (
        <div style={{ padding:'8px 16px', background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:8, marginBottom:20, display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ flex:1, fontSize:12, color:'#92400E' }}>⚡ Quick Quote — Planner only. No lifecycle tracking.</div>
          <button className="btn btn-ghost btn-sm" style={{ fontSize:11 }} onClick={() => setQuoteMode(null)}>Change</button>
        </div>
      )}

      {/* Step indicator */}
      {quoteMode !== null && <div style={{ display:'flex', alignItems:'center', marginBottom:32, overflowX:'auto', paddingBottom:4 }}>
        {STEPS.map((s, i) => (
          <React.Fragment key={i}>
            <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:5, cursor: i < step-1 ? 'pointer' : 'default' }}
              onClick={() => { if (i < step-1) setStep(i+1); }}>
              <div className={`step-dot ${i+1===step ? 'step-dot-active' : i+1<step ? 'step-dot-done' : 'step-dot-inactive'}`}>
                {i+1 < step ? <Check size={12} /> : i+1}
              </div>
              <span style={{ fontSize:11, fontWeight: i+1===step ? 600 : 400, color: i+1===step ? 'var(--gold)' : i+1<step ? 'var(--teal)' : 'var(--text-muted)', whiteSpace:'nowrap' }}>
                {s}
              </span>
            </div>
            {i < STEPS.length-1 && (
              <div style={{ flex:1, height:1, background: i+1<step ? 'var(--teal)' : 'var(--border)', margin:'0 6px', marginBottom:20, minWidth:14 }} />
            )}
          </React.Fragment>
        ))}
      </div>}

      {/* ── STEP 1: Service & Product ──────────────────────────── */}
      {quoteMode !== null && (quoteMode !== 'link-enquiry' || linkedEnquiry) && step === 1 && (
        <div>
          <div style={{ marginBottom:20 }}>
            <h2 className="gold-accent" style={{ marginBottom:4 }}>Service & Product</h2>
            <p style={{ color:'var(--text-secondary)', fontSize:13 }}>Select the service and product. You can add multiple packages for multi-event bookings.</p>
          </div>

          {/* Added packages */}
          {packages.length > 0 && (
            <div style={{ marginBottom:16 }}>
              {packages.map(pkg => (
                <div key={pkg.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'11px 14px', background:'#fff', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', marginBottom:6, boxShadow:'var(--shadow-sm)' }}>
                  <div style={{ flex:1 }}>
                    <span style={{ fontSize:13, fontWeight:600, marginRight:8 }}>{pkg.product.serviceName}</span>
                    <span className="badge badge-gold" style={{ marginRight:6 }}>{pkg.packageType}</span>
                    <span className={`badge ${pkg.product.tier==='Best'?'badge-best':pkg.product.tier==='Luxe'?'badge-luxe':'badge-elite'}`} style={{ marginRight:6 }}>{pkg.product.tier}</span>
                    <span style={{ fontSize:12, color:'var(--text-secondary)' }}>{pkg.product.productName}</span>
                  </div>
                  <span style={{ fontSize:11, color:'var(--text-muted)', fontFamily:'monospace' }}>{pkg.product.code}</span>
                  <button className="btn btn-ghost btn-sm btn-icon" onClick={() => removePackage(pkg.id)}>
                    <Trash2 size={13} color="var(--red)" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add form */}
          {showAddForm ? (
            <div className="card" style={{ border:'1.5px solid var(--border-light)' }}>

              {/* 1. Service */}
              <div style={{ marginBottom:20 }}>
                <div style={{ fontSize:12, fontWeight:700, color:'var(--text-secondary)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:10 }}>
                  Step 1 · Select Service
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8 }}>
                  {SERVICES.map(svc => {
                    const sel = draftService === svc.key;
                    return (
                      <button key={svc.key}
                        onClick={() => { setDraftService(svc.key); setDraftPackageType(''); setDraftTier(null); setDraftProductName(''); }}
                        style={{ padding:'11px 12px', borderRadius:'var(--radius-md)', cursor:'pointer', textAlign:'left', background: sel?'var(--gold-bg)':'#F7FAFC', border:`1.5px solid ${sel?'var(--gold)':'var(--border)'}`, fontFamily:'inherit', transition:'all 0.12s', boxShadow: sel?'0 0 0 3px rgba(183,121,31,0.1)':'none' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:7, marginBottom:2 }}>
                          <span style={{ color: sel?'var(--gold)':'var(--text-muted)' }}>{svc.icon}</span>
                          <span style={{ fontSize:13, fontWeight:600, color: sel?'var(--gold)':'var(--text-primary)' }}>{svc.label}</span>
                        </div>
                        <div style={{ fontSize:11, color:'var(--text-muted)', paddingLeft:22 }}>{svc.sub}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* 2. Package Type */}
              {draftService && (
                <div style={{ borderTop:'1px solid var(--border)', paddingTop:18, marginBottom: draftPackageType ? 20 : 0 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:'var(--text-secondary)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:10 }}>
                    Step 2 · Package Type
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    {[
                      { id:'Fixed', label:'Fixed Package', sub:'Studio decides everything — 3 album options shown to customer', icon:'🔵' },
                      { id:'Semi-Fixed', label:'Semi-Fixed Package', sub:'Camera fixed by studio — Post production + Album flexible for customer', icon:'🟡' },
                      { id:'Fully Personalized', label:'Fully Personalized', sub:'Customer builds from scratch — full freedom', icon:'🟣' },
                    ].map(pt => {
                      const sel = draftPackageType === pt.id;
                      return (
                        <button key={pt.id}
                          onClick={() => { setDraftPackageType(pt.id); setDraftTier(null); setDraftProductName(''); }}
                          style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 16px', borderRadius:'var(--radius-sm)', cursor:'pointer', textAlign:'left', background: sel?'var(--gold-bg)':'#F7FAFC', border:`1.5px solid ${sel?'var(--gold)':'var(--border)'}`, fontFamily:'inherit', transition:'all 0.12s', width:'100%', boxShadow: sel?'0 0 0 3px rgba(183,121,31,0.1)':'none' }}>
                          <span style={{ fontSize:18, flexShrink:0 }}>{pt.icon}</span>
                          <div style={{ flex:1 }}>
                            <div style={{ fontSize:13, fontWeight:600, color: sel?'var(--gold)':'var(--text-primary)' }}>{pt.label}</div>
                            <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:2 }}>{pt.sub}</div>
                          </div>
                          {sel && <Check size={15} style={{ color:'var(--gold)', flexShrink:0 }} />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* 3. Tier */}
              {draftPackageType && (
                <div style={{ borderTop:'1px solid var(--border)', paddingTop:18, marginBottom:20 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:'var(--text-secondary)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:10 }}>
                    Step 3 · Quality Tier
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8 }}>
                    {TIERS.filter(t => availableTiers.includes(t.id)).map(tier => {
                      const sel = draftTier === tier.id;
                      return (
                        <button key={tier.id}
                          onClick={() => { setDraftTier(tier.id); setDraftProductName(''); }}
                          style={{ padding:'12px', borderRadius:'var(--radius-md)', cursor:'pointer', textAlign:'left', background: sel?'var(--gold-bg)':'#F7FAFC', border:`1.5px solid ${sel?'var(--gold)':'var(--border)'}`, fontFamily:'inherit', transition:'all 0.12s', boxShadow: sel?'0 0 0 3px rgba(183,121,31,0.1)':'none' }}>
                          <div style={{ marginBottom:5 }}><span className={`badge ${tier.badge}`}>{tier.id}</span></div>
                          <div style={{ fontSize:11, color:'var(--text-muted)' }}>{tier.desc}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* 3. Product Name */}
              {draftTier && (
                <div style={{ borderTop:'1px solid var(--border)', paddingTop:18, marginBottom:20 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:'var(--text-secondary)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:10 }}>
                    Step 4 · Select Product
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                    {availableProductNames.map(name => {
                      const sel = draftProductName === name;
                      const prods = getProducts(draftService!, draftTier!, name, draftPackageType);
                      const hasVideo = prods.some(p => p.isVideoOnly);
                      const hasAlbum = prods.some(p => p.hasAlbum);
                      const codes = prods.map(p => p.code).join(' / ');
                      const isDestination = prods[0]?.isDestination;
                      const isTogetherness = prods[0]?.isTogetherness;
                      return (
                        <button key={name}
                          onClick={() => setDraftProductName(name)}
                          style={{ display:'flex', alignItems:'flex-start', gap:14, padding:'12px 16px', borderRadius:'var(--radius-sm)', cursor:'pointer', textAlign:'left', background: sel?'var(--gold-bg)':'#fff', border:`1.5px solid ${sel?'var(--gold)':'var(--border)'}`, fontFamily:'inherit', transition:'all 0.12s', width:'100%', boxShadow: sel?'0 0 0 3px rgba(183,121,31,0.1)':'var(--shadow-sm)' }}>
                          <div style={{ width:18, height:18, borderRadius:'50%', border:`2px solid ${sel?'var(--gold)':'var(--border-light)'}`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginTop:1 }}>
                            {sel && <div style={{ width:9, height:9, borderRadius:'50%', background:'var(--gold)' }} />}
                          </div>
                          <div style={{ flex:1 }}>
                            <div style={{ fontSize:13, fontWeight:600, color: sel?'var(--gold)':'var(--text-primary)', marginBottom:3 }}>{name}</div>
                            {(isDestination || isTogetherness) && (
                              <div style={{ fontSize:11, color:'var(--blue)', marginBottom:3 }}>
                                {isDestination ? '📍 Destination Wedding — 2 albums' : '💑 Togetherness — 2 albums (Bride + Groom side)'}
                              </div>
                            )}
                            <div style={{ fontSize:11, color:'var(--text-muted)' }}>
                              {hasAlbum ? '📷 Includes album' : hasVideo ? '🎥 Video only — no album' : ''}
                              {prods.length > 1 ? ` · Album / No-album variants available` : ''}
                            </div>
                            <div style={{ fontSize:10, color:'var(--text-muted)', marginTop:3, fontFamily:'monospace' }}>{codes}</div>
                          </div>
                          {sel && <Check size={15} style={{ color:'var(--gold)', flexShrink:0, marginTop:2 }} />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Add button */}
              <div style={{ display:'flex', gap:8, borderTop: resolvedProduct?'1px solid var(--border)':'none', paddingTop: resolvedProduct?16:0 }}>
                {packages.length > 0 && (
                  <button className="btn btn-secondary" onClick={() => { setShowAddForm(false); setDraftService(null); setDraftTier(null); setDraftProductName(''); }}>
                    Cancel
                  </button>
                )}
                <button className="btn btn-primary" onClick={addPackage} disabled={!resolvedProduct}>
                  <Check size={14} /> Add Package
                </button>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowAddForm(true)}
              style={{ width:'100%', padding:'11px', borderRadius:'var(--radius-md)', border:'1.5px dashed var(--border-light)', background:'#fff', color:'var(--text-secondary)', cursor:'pointer', fontSize:13, display:'flex', alignItems:'center', justifyContent:'center', gap:6, fontFamily:'inherit' }}>
              <Plus size={14} /> Add Another Package
            </button>
          )}

          {canNext1 && (
            <div style={{ display:'flex', justifyContent:'flex-end', marginTop:24 }}>
              <button className="btn btn-primary btn-lg" onClick={() => setStep(2)}>
                Continue <ChevronRight size={15} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── STEP 2: Customer Info ──────────────────────────────── */}
      {step === 2 && postEnquiryStep !== 'choose' && (
        <div className="card">
          <h2 className="gold-accent" style={{ marginBottom:16 }}>Customer Details</h2>
          <div className="form-row" style={{ marginBottom:12 }}>
            <div className="form-group" style={{ marginBottom:0 }}>
              <label>Customer Name *</label>
              <input className="input" placeholder="Full name" value={customerName} onChange={e => setCustomerName(e.target.value)} />
            </div>
            <div className="form-group" style={{ marginBottom:0 }}>
              <label>Phone Number *</label>
              <input className="input" placeholder="10-digit mobile" value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group" style={{ marginBottom:0 }}>
              <label>Email (optional)</label>
              <input className="input" type="email" placeholder="email@example.com" value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} />
            </div>
            <div className="form-group" style={{ marginBottom:0 }}>
              <label>Event Name *</label>
              <input className="input" placeholder="e.g. Rahul & Priya Wedding" value={eventName} onChange={e => setEventName(e.target.value)} />
            </div>
          </div>
          {quoteMode === 'new-lifecycle' && enquiryCreateError && (
            <div style={{ padding:'10px 14px', background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:8, marginBottom:14, fontSize:12.5, color:'#7F1D1D' }}>
              ⚠ Couldn't create the enquiry yet: <strong>{enquiryCreateError}</strong> — you can still fix the details above and try Continue again.
            </div>
          )}
          <div style={{ display:'flex', justifyContent:'space-between', marginTop:24 }}>
            <button className="btn btn-secondary" onClick={() => setStep(1)}><ChevronLeft size={14} /> Back</button>
            <button className="btn btn-primary btn-lg" disabled={!canNext2 || enquiryCreating} onClick={async () => {
                if (quoteMode !== 'new-lifecycle' || autoEnquiry) { setStep(3); return; }
                // Enquiry gets created here — right after Customer details are
                // filled, before the rest of the wizard — so a real,
                // follow-up-able Enquiry Tracker record exists immediately,
                // even if this quote session gets abandoned before Summary.
                setEnquiryCreating(true);
                setEnquiryCreateError('');
                try {
                  const serviceKey = packages[0]?.product.serviceKey;
                  const service_type = (serviceKey && SERVICE_KEY_TO_ENQUIRY_TYPE[serviceKey]) || 'Any Event';
                  const created = await createEnquiry({
                    service_type,
                    customer_name: customerName,
                    contact_number: customerPhone,
                    email: customerEmail || null,
                    contact_via: 'Planner',
                    suggested_package: packages[0] ? `${packages[0].product.productName} (${packages[0].product.tier})` : null,
                    follow_up_status: 'Pending',
                    overall_status: 'Open',
                  });
                  setAutoEnquiry({ id: created.id, ref_number: created.ref_number });
                  setPostEnquiryStep('choose');
                } catch (e: any) {
                  setEnquiryCreateError(e?.message || 'Unknown error');
                }
                setEnquiryCreating(false);
              }}>
              {enquiryCreating ? 'Creating enquiry…' : 'Continue'} <ChevronRight size={15} />
            </button>
          </div>
        </div>
      )}

      {/* ── Post-enquiry choice: new quote now, or attach one built separately ── */}
      {step === 2 && postEnquiryStep === 'choose' && autoEnquiry && (
        <div className="card">
          <div style={{ padding:'10px 14px', background:'rgba(56,178,172,0.08)', border:'1.5px solid var(--teal)', borderRadius:8, marginBottom:20, fontSize:13, color:'var(--teal)' }}>
            ✓ Enquiry <strong>{autoEnquiry.ref_number}</strong> created for {customerName}. What next?
          </div>
          {!showAttachExisting ? (
            <div style={{ display:'flex', gap:16, flexWrap:'wrap' }}>
              <div onClick={() => { setPostEnquiryStep(null); setStep(3); }}
                style={{ flex:'1 1 260px', padding:24, borderRadius:12, border:'2px solid var(--border)', cursor:'pointer', background:'#fff' }}
                onMouseEnter={e=>(e.currentTarget as HTMLDivElement).style.borderColor='var(--gold)'}
                onMouseLeave={e=>(e.currentTarget as HTMLDivElement).style.borderColor='var(--border)'}>
                <div style={{ fontSize:28, marginBottom:10 }}>📝</div>
                <div style={{ fontWeight:700, fontSize:15, marginBottom:6 }}>Build New Quote</div>
                <div style={{ fontSize:12.5, color:'var(--text-secondary)' }}>Continue the wizard now — Event Dates, Contractor, PP Vendor, Album, Add-ons, Summary.</div>
              </div>
              <div onClick={() => setShowAttachExisting(true)}
                style={{ flex:'1 1 260px', padding:24, borderRadius:12, border:'2px solid var(--border)', cursor:'pointer', background:'#fff' }}
                onMouseEnter={e=>(e.currentTarget as HTMLDivElement).style.borderColor='var(--teal)'}
                onMouseLeave={e=>(e.currentTarget as HTMLDivElement).style.borderColor='var(--border)'}>
                <div style={{ fontSize:28, marginBottom:10 }}>🔗</div>
                <div style={{ fontWeight:700, fontSize:15, marginBottom:6 }}>Attach Existing Quote</div>
                <div style={{ fontSize:12.5, color:'var(--text-secondary)' }}>Already built one for this customer separately (e.g. via Quick Quote)? Link it here instead of building again.</div>
              </div>
            </div>
          ) : attachedQuote ? (
            <div style={{ padding:'14px 18px', background:'#D1FAE5', border:'1px solid #6EE7B7', borderRadius:8, fontSize:13, color:'#065F46' }}>
              ✓ Linked <strong>{attachedQuote.quoteNumber}</strong> to {autoEnquiry.ref_number}.
              <div style={{ marginTop:10 }}>
                <button className="btn btn-primary btn-sm" onClick={() => onSaved()}>Done</button>
              </div>
            </div>
          ) : (
            <div>
              <button className="btn btn-ghost btn-sm" style={{ marginBottom:10 }} onClick={() => setShowAttachExisting(false)}>← Back</button>
              <input className="input" placeholder="Search by quote number or customer name..." value={attachSearch}
                onChange={e => setAttachSearch(e.target.value)} style={{ marginBottom:12 }} autoFocus />
              {(() => {
                const quotes = loadFromStorage<Quote[]>('ss_quotes', []);
                const filtered = quotes.filter(q =>
                  !attachSearch ||
                  q.customerName?.toLowerCase().includes(attachSearch.toLowerCase()) ||
                  q.quoteNumber?.toLowerCase().includes(attachSearch.toLowerCase())
                );
                if (filtered.length === 0) return <div style={{ fontSize:13, color:'var(--text-muted)', padding:20, textAlign:'center' }}>No quotes found. You can still "Build New Quote" instead.</div>;
                return filtered.map(q => (
                  <div key={q.id} onClick={() => handleAttachExisting(q)}
                    style={{ padding:'12px 16px', borderRadius:8, marginBottom:8, cursor:'pointer', border:'1.5px solid var(--border)', background:'#fff' }}
                    onMouseEnter={e=>(e.currentTarget as HTMLDivElement).style.borderColor='var(--teal)'}
                    onMouseLeave={e=>(e.currentTarget as HTMLDivElement).style.borderColor='var(--border)'}>
                    <div style={{ fontWeight:600, fontSize:14 }}>{q.customerName}</div>
                    <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:2 }}>{q.quoteNumber} · ₹{(q.quotedAmount||0).toLocaleString('en-IN')}</div>
                  </div>
                ));
              })()}
            </div>
          )}
        </div>
      )}

      {/* ── STEP 3: Event Dates ────────────────────────────────── */}
      {step === 3 && (
        <div>
          <h2 className="gold-accent" style={{ marginBottom:6 }}>Event Dates</h2>
          <p style={{ color:'var(--text-secondary)', fontSize:13, marginBottom:20 }}>Add the event date(s) for each package.</p>
          {packages.map(pkg => (
            <div key={pkg.id} className="card" style={{ marginBottom:14 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:14 }}>
                <Calendar size={14} color="var(--gold)" />
                <span style={{ fontWeight:600, fontSize:14 }}>{pkg.product.serviceName}</span>
                <span className={`badge ${pkg.product.tier==='Best'?'badge-best':pkg.product.tier==='Luxe'?'badge-luxe':'badge-elite'}`}>{pkg.product.tier}</span>
                <span style={{ fontSize:12, color:'var(--text-secondary)' }}>— {pkg.product.productName}</span>
              </div>
              {pkg.eventDates.map((ed, i) => (
                <div key={i} style={{ display:'flex', gap:10, marginBottom:8 }}>
                  <input type="date" className="input" value={ed.date} onChange={e => {
                    const dates = [...pkg.eventDates];
                    dates[i] = { ...dates[i], date: e.target.value };
                    updatePackage(pkg.id, { eventDates: dates });
                  }} />
                  <input className="input" placeholder="Label e.g. Wedding Day, Mehendi" value={ed.label} onChange={e => {
                    const dates = [...pkg.eventDates];
                    dates[i] = { ...dates[i], label: e.target.value };
                    updatePackage(pkg.id, { eventDates: dates });
                  }} />
                  <button className="btn btn-ghost btn-sm btn-icon" onClick={() => {
                    updatePackage(pkg.id, { eventDates: pkg.eventDates.filter((_, j) => j !== i) });
                  }}><Trash2 size={12} color="var(--red)" /></button>
                </div>
              ))}
              <button className="btn btn-ghost btn-sm" onClick={() =>
                updatePackage(pkg.id, { eventDates: [...pkg.eventDates, { date:'', label:`Day ${pkg.eventDates.length+1}` }] })
              } style={{ borderStyle:'dashed', borderColor:'var(--border)', width:'100%', justifyContent:'center', marginTop:4 }}>
                <Plus size={12} /> Add Date
              </button>
            </div>
          ))}
          {/* Multi-day note */}
          <div className="alert alert-info" style={{ marginBottom:12 }}>
            💡 Contractor rate is multiplied by number of event days automatically.
          </div>
          <div style={{ display:'flex', justifyContent:'space-between', marginTop:8 }}>
            <button className="btn btn-secondary" onClick={() => setStep(2)}><ChevronLeft size={14} /> Back</button>
            <button className="btn btn-primary btn-lg" onClick={() => {
              // Auto-calculate numDays for each package from event dates count
              setPackages(prev => prev.map(p => ({
                ...p,
                numDays: Math.max(1, p.eventDates.filter(d => d.date).length),
              })));
              setStep(4);
            }} disabled={!canNext3}>
              Continue <ChevronRight size={15} />
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 4: Contractor ──────────────────────────────── */}
      {step === 4 && (
        <div>
          <h2 className="gold-accent" style={{ marginBottom:6 }}>Contractor</h2>
          <p style={{ color:'var(--text-secondary)', fontSize:13, marginBottom:20 }}>
            Fixed &amp; Semi-Fixed — contractor auto-assigned. Fully Personalized — select contractor and deliverables.
            {packages.some(p => p.packageType === 'Fully Personalized' && p.fpCameraQuality) && (
              <span style={{ display:'block', marginTop:4, color:'var(--blue)', fontSize:12 }}>
                FP packages: showing contractors matching selected camera quality tier.
              </span>
            )}
          </p>
          {packages.map(pkg => {
            const isFixed = pkg.packageType === 'Fixed' || pkg.packageType === 'Semi-Fixed';
            const effectiveTier = (pkg.packageType === 'Fully Personalized' && pkg.fpCameraQuality)
              ? pkg.fpCameraQuality as 'Best'|'Luxe'|'Elite'
              : pkg.product.tier;
            const validContractors = CONTRACTORS_BY_TIER[effectiveTier].filter(name => name !== 'Monk Films');
            const numDays = Math.max(1, pkg.eventDates.filter(d => d.date).length);

            // No auto-select for Fixed — user clicks contractor card
            return (
              <div key={pkg.id} className="card" style={{ marginBottom:14 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:14 }}>
                  <User size={14} color="var(--gold)" />
                  <span style={{ fontWeight:600, fontSize:14 }}>{pkg.product.serviceName}</span>
                  <span className={`badge ${pkg.product.tier==='Best'?'badge-best':pkg.product.tier==='Luxe'?'badge-luxe':'badge-elite'}`}>{pkg.product.tier}</span>
                  <span style={{ fontSize:12, color:'var(--text-secondary)' }}>— {pkg.product.productName}</span>
                </div>
                {isFixed ? (
                  /* Fixed — choose contractor (deliverables auto-load per contractor) */
                  <div>
                    <div className="alert alert-info" style={{ marginBottom:12 }}>
                      📅 {numDays} event day(s) detected — select contractor below. All rates × {numDays} day(s) calculated live.
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                      {validContractors.map(cName => {
                        const isSel = pkg.contractor === cName;
                        const allDelivs = CONTRACTORS.filter(c =>
                          c.contractor === cName && c.status === 'Active' &&
                          (c.rates as any)[pkg.product.serviceKey] !== undefined &&
                          (c.category !== 'Add-On' || (
                            (c.deliverable.toLowerCase().includes('drone') && pkg.product.deliverables?.toLowerCase().includes('drone')) ||
                            (c.deliverable.toLowerCase().includes('bts') && pkg.product.deliverables?.toLowerCase().includes('bts'))
                          )) &&
                          // Only this product's own category — plus the two cross-cutting
                          // categories some contractors use instead of duplicating per
                          // category: 'Cinematography' (Gunabh/Faizul/Uttam Rai/Guru's
                          // Standard/High-End pair) and 'All Packages' (Jitendra, flat rate
                          // regardless of package type).
                          (matchesContractorCategory(c.category, pkg.product.contractorCategory) || c.category === 'All Packages' ||
                          // Cinematography only for packages that include video (not photo-only)
                          (c.category === 'Cinematography' && (
                            pkg.product.contractorCategory?.includes('Photo+Video') ||
                            pkg.product.contractorCategory?.includes('Exclusive') ||
                            pkg.product.contractorCategory?.includes('Video') ||
                            pkg.product.contractorCategory === 'Cinematography'
                          )) ||
                          (c.category === 'Add-On' && (
                            (c.deliverable.toLowerCase().includes('drone') && pkg.product.deliverables?.toLowerCase().includes('drone')) ||
                            (c.deliverable.toLowerCase().includes('bts') && pkg.product.deliverables?.toLowerCase().includes('bts'))
                          )))
                        ).map(c => ({
                          deliverable: c.deliverable,
                          rate: (c.rates as any)[pkg.product.serviceKey] as number,
                          ratePerDay: (c.rates as any)[pkg.product.serviceKey] as number,
                          isCineOption: c.deliverable === 'Cinematography' || c.deliverable === 'Cinematography — High-End',
                        })).filter((v, i, a) => a.findIndex(x => x.deliverable === v.deliverable) === i);

                        // For Cinematography radio pair: auto-select based on tier
                        // Elite → High-End pre-selected. Best/Luxe → standard pre-selected.
                        const defaultCine = effectiveTier === 'Elite' ? 'Cinematography — High-End' : 'Cinematography';
                        const hasCinePair = allDelivs.some(d => d.deliverable === 'Cinematography') && 
                                            allDelivs.some(d => d.deliverable === 'Cinematography — High-End');
                        
                        // Build final deliverables: non-cine ones all included, cine = only the default selected
                        const cDelivs = hasCinePair
                          ? allDelivs.filter(d => !d.isCineOption || d.deliverable === defaultCine)
                          : allDelivs;
                        const total = cDelivs.reduce((s,d) => s + d.ratePerDay, 0); // per-day total, days applied in totalCost

                        // What to actually SHOW once this card is selected: the real saved
                        // selection (pkg.selectedDeliverables), not the tier default recomputed
                        // from scratch. Without this, tapping Standard/High-End saves correctly
                        // but the screen always snaps back to showing the tier default.
                        const liveDelivs = (isSel && (pkg.selectedDeliverables||[]).length > 0)
                          ? pkg.selectedDeliverables.map(d => ({
                              deliverable: d.deliverable,
                              rate: d.rate,
                              ratePerDay: d.ratePerDay || d.rate,
                              isCineOption: d.deliverable === 'Cinematography' || d.deliverable === 'Cinematography — High-End',
                            }))
                          : cDelivs;
                        return (
                          <div key={cName} style={{ border:`1.5px solid ${isSel?'var(--teal)':'var(--border)'}`, borderRadius:'var(--radius-md)', overflow:'hidden', cursor:'pointer', boxShadow: isSel?'0 0 0 3px rgba(15,110,86,0.1)':'var(--shadow-sm)' }}
                            onClick={(e) => {
                              // Only trigger if clicking the header area, not the deliverables
                              if ((e.target as HTMLElement).closest('[data-deliverables]')) return;
                              // Already selected — clicking the header again must not reset
                              // a High-End pick back to the tier default.
                              if (pkg.contractor === cName) return;
                              // Pre-select the default cine option based on tier
                              const finalDlvs = hasCinePair
                                ? [...cDelivs.filter(d => !d.isCineOption),
                                   ...(allDelivs.filter(d => d.deliverable === defaultCine))]
                                : cDelivs;
                              const finalTotal = finalDlvs.reduce((s,d)=>s+(d.ratePerDay||d.rate),0);
                              updatePackage(pkg.id, { contractor: cName, contractorRate: finalTotal, selectedDeliverables: finalDlvs });
                            }}>
                            <div style={{ padding:'11px 16px', background: isSel?'var(--teal-bg)':'#F7FAFC', borderBottom: isSel?'1px solid #9AE6B4':'1px solid var(--border)', display:'flex', alignItems:'center', gap:10 }}>
                              <div style={{ width:30, height:30, borderRadius:'50%', background: isSel?'var(--teal)':'#E2E8F0', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:700, color: isSel?'#fff':'var(--text-muted)', flexShrink:0 }}>
                                {cName[0]}
                              </div>
                              <span style={{ fontSize:14, fontWeight:700, color: isSel?'var(--teal)':'var(--text-primary)', flex:1 }}>{cName}</span>
                              <span style={{ fontSize:13, fontWeight:700, color: isSel?'var(--teal)':'var(--text-secondary)' }}>{formatCurrency(liveDelivs.reduce((s,d)=>s+d.ratePerDay,0))} × {numDays} day(s)</span>
                              {isSel && <Check size={15} color="var(--teal)" />}
                            </div>
                            {isSel && (
                              <div style={{ padding:'6px 12px' }}>
                                <div data-deliverables="true" onClick={e => e.stopPropagation()}>
                                {/* Show all non-cine deliverables */}
                                {liveDelivs.filter(d => d.deliverable !== 'Cinematography' && d.deliverable !== 'Cinematography — High-End').map((d, di) => (
                                  <div key={di} style={{ display:'flex', justifyContent:'space-between', padding:'6px 6px', fontSize:12, borderBottom:'1px solid var(--border)' }}>
                                    <span style={{ color:'var(--teal)' }}>✓ {d.deliverable}</span>
                                    <span style={{ color:'var(--text-secondary)' }}>₹{d.ratePerDay.toLocaleString('en-IN')}/day × {numDays} = <strong>{formatCurrency(d.ratePerDay * numDays)}</strong></span>
                                  </div>
                                ))}
                                {/* Cine RADIO - either standard OR high-end */}
                                {hasCinePair && (() => {
                                  const selCine = liveDelivs.find(d => d.isCineOption);
                                  const selCineName = selCine?.deliverable || defaultCine;
                                  return (
                                    <div style={{ padding:'8px 6px', borderTop:'1px solid var(--border)', background:'#FFFBEB', borderRadius:4 }}>
                                      <div style={{ fontSize:11, color:'var(--amber)', fontWeight:600, marginBottom:6 }}>
                                        🎬 Cinematography — select one:
                                      </div>
                                      <div style={{ display:'flex', gap:8 }}>
                                        {[
                                          { name:'Cinematography', rate: (allDelivs.find(d=>d.deliverable==='Cinematography')?.ratePerDay||6000) },
                                          { name:'Cinematography — High-End', rate: (allDelivs.find(d=>d.deliverable==='Cinematography — High-End')?.ratePerDay||9000) },
                                        ].map(opt => {
                                          const isSel = selCineName === opt.name;
                                          return (
                                            <button key={opt.name}
                                              onClick={(e) => {
                                                e.stopPropagation(); // prevent card click from firing
                                                // Replace cine in selectedDeliverables - keep all non-cine deliverables
                                                const nonCine = (pkg.selectedDeliverables||[])
                                                  .filter(d => d.deliverable !== 'Cinematography' && d.deliverable !== 'Cinematography — High-End');
                                                const newDlvs = [...nonCine, { deliverable: opt.name, rate: opt.rate, ratePerDay: opt.rate }];
                                                const total = newDlvs.reduce((s,d)=>s+(d.ratePerDay||d.rate),0);
                                                updatePackage(pkg.id, { contractor: cName, contractorRate: total, selectedDeliverables: newDlvs });
                                              }}
                                              style={{ flex:1, padding:'8px 10px', borderRadius:'var(--radius-sm)', cursor:'pointer', fontFamily:'inherit', fontSize:12, fontWeight:isSel?700:400, background:isSel?'var(--gold-bg)':'#fff', border:`1.5px solid ${isSel?'var(--gold)':'var(--border)'}`, color:isSel?'var(--gold)':'var(--text-secondary)', textAlign:'left' }}>
                                              <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                                                <div style={{ width:14, height:14, borderRadius:'50%', border:`2px solid ${isSel?'var(--gold)':'var(--border-light)'}`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                                                  {isSel && <div style={{ width:7, height:7, borderRadius:'50%', background:'var(--gold)' }}/>}
                                                </div>
                                                <span>{opt.name === 'Cinematography' ? 'Standard' : 'High-End'}</span>
                                              </div>
                                              <div style={{ fontSize:11, color:isSel?'var(--gold)':'var(--text-muted)', marginTop:2, paddingLeft:20 }}>
                                                ₹{opt.rate.toLocaleString('en-IN')}/day × {numDays} = {formatCurrency(opt.rate * numDays)}
                                              </div>
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  );
                                })()}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  /* Semi-Fixed / FP — manual selection with deliverable checkboxes */
                  <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
                  {validContractors.map(contractorName => {
                    const isContractorSel = pkg.contractor === contractorName;
                    // Get ALL deliverables for this contractor + product category + service
                    const contractorDeliverables = CONTRACTORS.filter(c =>
                      c.contractor === contractorName &&
                      c.status === 'Active' &&
                      (c.rates as any)[pkg.product.serviceKey] !== undefined &&
                      (c.category !== 'Add-On' || (
                        (c.deliverable.toLowerCase().includes('drone') && pkg.product.deliverables?.toLowerCase().includes('drone')) ||
                        (c.deliverable.toLowerCase().includes('bts') && pkg.product.deliverables?.toLowerCase().includes('bts'))
                      )) &&
                      (matchesContractorCategory(c.category, pkg.product.contractorCategory) || c.category === 'All Packages' ||
                      (c.category === 'Cinematography' && (
                        pkg.product.contractorCategory?.includes('Photo+Video') ||
                        pkg.product.contractorCategory?.includes('Exclusive') ||
                        pkg.product.contractorCategory?.includes('Video') ||
                        pkg.product.contractorCategory === 'Cinematography'
                      )) ||
                      (c.category === 'Add-On' && (
                        (c.deliverable.toLowerCase().includes('drone') && pkg.product.deliverables?.toLowerCase().includes('drone')) ||
                        (c.deliverable.toLowerCase().includes('bts') && pkg.product.deliverables?.toLowerCase().includes('bts'))
                      )))
                    ).map(c => ({
                      deliverable: c.deliverable,
                      rate: (c.rates as any)[pkg.product.serviceKey] as number,
                      category: c.category,
                    })).filter((v, i, a) => a.findIndex(x => x.deliverable === v.deliverable) === i);

                    if (!contractorDeliverables.length) return null;

                    const totalForContractor = (pkg.selectedDeliverables || [])
                      .filter(d => pkg.contractor === contractorName)
                      .reduce((s, d) => s + d.rate * (d.qty || 1), 0);

                    return (
                      <div key={contractorName} style={{ border:`1.5px solid ${isContractorSel?'var(--teal)':'var(--border)'}`, borderRadius:'var(--radius-md)', overflow:'hidden', boxShadow: isContractorSel?'0 0 0 3px rgba(15,110,86,0.1)':'var(--shadow-sm)' }}>
                        {/* Contractor header */}
                        <div style={{ padding:'12px 16px', background: isContractorSel?'var(--teal-bg)':'#F7FAFC', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', gap:10 }}>
                          <div style={{ width:32, height:32, borderRadius:'50%', background: isContractorSel?'var(--teal)':'#E2E8F0', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:700, color: isContractorSel?'#fff':'var(--text-muted)', flexShrink:0 }}>
                            {contractorName[0]}
                          </div>
                          <span style={{ fontSize:14, fontWeight:700, color: isContractorSel?'var(--teal)':'var(--text-primary)', flex:1 }}>{contractorName}</span>
                          {isContractorSel && totalForContractor > 0 && (
                            <span style={{ fontSize:13, fontWeight:700, color:'var(--teal)' }}>{formatCurrency(totalForContractor)}</span>
                          )}
                        </div>
                        {/* Deliverables */}
                        <div style={{ padding:'8px 12px', display:'flex', flexDirection:'column', gap:4 }}>
                          {contractorDeliverables.map((item, di) => {
                            const selectedEntry = isContractorSel ? (pkg.selectedDeliverables||[]).find(d => d.deliverable === item.deliverable) : undefined;
                            const isDlvSel = !!selectedEntry;
                            const qty = selectedEntry?.qty || 1;

                            const toggleDeliverable = () => {
                              // Select this contractor + toggle this deliverable
                              const currentDlvs = pkg.contractor === contractorName ? (pkg.selectedDeliverables||[]) : [];
                              const exists = currentDlvs.some(d => d.deliverable === item.deliverable);
                              const newDlvs = exists
                                ? currentDlvs.filter(d => d.deliverable !== item.deliverable)
                                : [...currentDlvs, { deliverable: item.deliverable, rate: item.rate, ratePerDay: item.rate, qty: 1 }];
                              const total = newDlvs.reduce((s,d) => s + d.rate * (d.qty||1), 0);
                              updatePackage(pkg.id, { contractor: contractorName, contractorRate: total, selectedDeliverables: newDlvs });
                            };

                            const updateQty = (newQty: number) => {
                              const clamped = Math.max(1, newQty);
                              const newDlvs = (pkg.selectedDeliverables||[]).map(d => d.deliverable === item.deliverable ? { ...d, qty: clamped } : d);
                              const total = newDlvs.reduce((s,d) => s + d.rate * (d.qty||1), 0);
                              updatePackage(pkg.id, { contractorRate: total, selectedDeliverables: newDlvs });
                            };

                            return (
                              <div key={di} onClick={toggleDeliverable}
                                style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'9px 10px', borderRadius:'var(--radius-sm)', cursor:'pointer', background: isDlvSel?'rgba(15,110,86,0.08)':'transparent', border:`1px solid ${isDlvSel?'var(--teal)':'transparent'}`, fontFamily:'inherit', transition:'all 0.12s', width:'100%' }}>
                                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                                  <div style={{ width:18, height:18, borderRadius:4, border:`2px solid ${isDlvSel?'var(--teal)':'var(--border-light)'}`, background: isDlvSel?'var(--teal)':'#fff', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                                    {isDlvSel && <Check size={11} color="white" />}
                                  </div>
                                  <span style={{ fontSize:13, color: isDlvSel?'var(--teal)':'var(--text-primary)', fontWeight: isDlvSel?600:400, textAlign:'left' }}>{item.deliverable}</span>
                                </div>
                                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                                  {isDlvSel && (
                                    <div onClick={e => e.stopPropagation()} style={{ display:'flex', alignItems:'center', gap:4, background:'#fff', border:'1px solid var(--border)', borderRadius:6, padding:'2px 4px' }}>
                                      <button onClick={() => updateQty(qty - 1)} disabled={qty<=1} style={{ width:18, height:18, border:'none', background:'transparent', cursor: qty<=1?'default':'pointer', color:'var(--text-secondary)', fontSize:13, lineHeight:1, fontFamily:'inherit' }}>−</button>
                                      <span style={{ fontSize:12, minWidth:14, textAlign:'center' }}>{qty}</span>
                                      <button onClick={() => updateQty(qty + 1)} style={{ width:18, height:18, border:'none', background:'transparent', cursor:'pointer', color:'var(--text-secondary)', fontSize:13, lineHeight:1, fontFamily:'inherit' }}>+</button>
                                    </div>
                                  )}
                                  <span style={{ fontSize:13, fontWeight:600, color: isDlvSel?'var(--teal)':'var(--text-secondary)', flexShrink:0, minWidth:70, textAlign:'right' }}>{formatCurrency(item.rate * qty)}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                  </div>
                )}
            </div>
            );
          })}
          <div style={{ display:'flex', justifyContent:'space-between', marginTop:8 }}>
            <button className="btn btn-secondary" onClick={() => setStep(3)}><ChevronLeft size={14} /> Back</button>
            <button className="btn btn-primary btn-lg" onClick={() => setStep(5)} disabled={!canNext4}>
              Continue <ChevronRight size={15} />
            </button>
          </div>
        </div>
      )}


      {/* ── STEP 5: PP Vendor ─────────────────────────────── */}
      {step === 5 && (() => {
        const PRODUCT_PP_DELIVERABLES: Record<string,string[]> = {
          'basic video session_wedding':['Full Event Video'],
          'basic package_wedding':['Complete Wedding Video','Wedding Highlights'],
          'premium session_luxe_wedding':['Complete Wedding Video','Wedding Highlights','Wedding Teaser','Instagram Reels'],
          'premium session_elite_wedding':['Complete Wedding Video','Wedding Highlights','Wedding Teaser','Instagram Reels','Short Film'],
          'exclusive session_luxe_wedding':['Complete Wedding Video','Wedding Highlights','Wedding Teaser','Instagram Reels','Cinematic Highlight Video'],
          'exclusive session_elite_wedding':['Complete Wedding Video','Wedding Highlights','Wedding Teaser','Instagram Reels','Short Film','Cinematic Highlight Video'],
          'basic package - extended_wedding':['Complete Wedding Video','Wedding Highlights'],
          'premium session - extended_luxe_wedding':['Complete Wedding Video','Wedding Highlights','Wedding Teaser','Instagram Reels'],
          'premium session - extended_elite_wedding':['Complete Wedding Video','Wedding Highlights','Wedding Teaser','Instagram Reels','Short Film'],
          'exclusive session - extended_luxe_wedding':['Complete Wedding Video','Wedding Highlights','Wedding Teaser','Instagram Reels','Cinematic Highlight Video'],
          'exclusive session - extended_elite_wedding':['Complete Wedding Video','Wedding Highlights','Wedding Teaser','Instagram Reels','Short Film','Cinematic Highlight Video'],
          'basic video session_engagement':['Full Event Video'],
          'basic package_engagement':['Complete Wedding Video','Wedding Highlights'],
          'premium session_luxe_engagement':['Complete Wedding Video','Wedding Highlights','Wedding Teaser','Instagram Reels'],
          'premium session_elite_engagement':['Complete Wedding Video','Wedding Highlights','Wedding Teaser','Instagram Reels','Short Film'],
          'exclusive session_luxe_engagement':['Complete Wedding Video','Wedding Highlights','Wedding Teaser','Instagram Reels','Cinematic Highlight Video'],
          'exclusive session_elite_engagement':['Complete Wedding Video','Wedding Highlights','Wedding Teaser','Instagram Reels','Short Film','Cinematic Highlight Video'],
          'basic video session_birthday':['Full Event Video'],
          'basic package_birthday':['Complete Event Video','Event Highlights'],
          'premium session_birthday':['Complete Event Video','Event Highlights','Teaser','Instagram Reels'],
          'exclusive session_birthday':['Complete Event Video','Event Highlights','Teaser','Instagram Reels','Cinematic Highlight Video'],
          'basic video session_office_event':['Full Event Video'],
          'basic package_office_event':['Complete Event Video','Event Highlights'],
          'premium session_office_event':['Complete Event Video','Event Highlights','Teaser','Instagram Reels'],
          'exclusive session_office_event':['Complete Event Video','Event Highlights','Teaser','Instagram Reels'],
          'premium package_pre_wedding':['Teaser','Scripted Cinematic Video','Instagram Reels','Digital Wedding Invitation'],
          'exclusive package_pre_wedding':['Teaser','Scripted Cinematic Video','Instagram Reels','Digital Wedding Invitation'],
          'premium photography session_pre_wedding':['Teaser','Scripted Cinematic Video','Instagram Reels','Digital Wedding Invitation'],
        };
        const EXCEL_TO_VENDOR: Record<string,Record<string,string[]>> = {
          'Complete Wedding Video':{Gunabh:['Full Event Video (per hr) Single Cam'],Shankar:['Full Event Video + Highlight','Full Event Video (per hr)']},
          'Full Event Video':{Gunabh:['Full Event Video (per hr) Single Cam'],Shankar:['Full Event Video (per hr)','Full Event Video (Fixed)']},
          'Complete Event Video':{Gunabh:['Full Event Video (per hr) Single Cam'],Shankar:['Full Event Video + Highlight','Full Event Video (Fixed)']},
          'Wedding Highlights':{Gunabh:['Teaser + Highlight'],Shankar:['Wedding Highlight']},
          'Event Highlights':{Gunabh:['Teaser + Highlight'],Shankar:['Wedding Highlight']},
          'Wedding Teaser':{Gunabh:['Teaser + Highlight'],Shankar:['Wedding Teaser']},
          'Teaser':{Gunabh:['Teaser + Highlight'],Shankar:['Wedding Teaser','Teaser']},
          'Cinematic Highlight Video':{Gunabh:['Teaser + Highlight'],Shankar:[]},
          'Instagram Reels':{Gunabh:['Instagram Reel'],Shankar:['Instagram Reel','On Event Reels (per reel)']},
          'Short Film':{Gunabh:['Short Film (~45 mins)'],Shankar:[]},
          'Scripted Cinematic Video':{Gunabh:['Cinematography (Scripted)'],Shankar:['Cinematography (Scripted)']},
          'Digital Wedding Invitation':{Gunabh:['Wedding Invitation'],Shankar:['Digital Wedding Invitation']},
        };
        const SHANKAR_TIER_CATS: Record<string,string[]> = {Best:['Basic','All Packages','Extras'],Luxe:['Basic','Premium','All Packages','Extras'],Elite:['Basic','Premium','Exclusive','All Packages','Extras']};
        const ppVendorNames = Array.from(new Set(PP_VENDORS.map((v:any)=>v.vendor)));
        return (
          <div>
            <h2 className="gold-accent" style={{marginBottom:6}}>Post-Production Vendor</h2>
            <p style={{color:'var(--text-secondary)',fontSize:13,marginBottom:20}}>Deliverables auto-determined from your package. Gunabh: toggle Instant for faster turnaround at higher rates.</p>
            {packages.map((pkg:any,pi:number)=>{
              const serviceKey=pkg.product.serviceKey as string;
              const tier=pkg.product.tier as string;
              const nameLower=pkg.product.productName.toLowerCase().trim();
              const pkgKey=nameLower+'_'+tier.toLowerCase()+'_'+serviceKey;
              const pkgKey2=nameLower+'_'+serviceKey;
              const excelDelivs:string[]=PRODUCT_PP_DELIVERABLES[pkgKey]||PRODUCT_PP_DELIVERABLES[pkgKey2]||[];
              // Only SF/FP are freeform. Fixed packages with no PP deliverables = photo-only, show nothing (not all)
              const isFreeForm=pkg.packageType==='Semi-Fixed'||pkg.packageType==='Fully Personalized';
              const curVendor=selectedPPVendor[pkg.id]||'';
              const isInstant=isInstantDelivery[pkg.id]||false;
              const curSels=pkgPPVendors[pkg.id]||[];
              const pe=photoEditCharge[pkg.id]||{enabled:false,rate:50,qty:0};
              const ppDeliverableCost=curSels.reduce((s:number,v:any)=>s+v.rate*(v.qty||1),0);
              const photoEditCost=pe.enabled?(pe.rate||50)*(pe.qty||0):0;
              const ppTotal=ppDeliverableCost+photoEditCost;
              const prefCat=({Best:'Basic',Luxe:'Premium',Elite:'Exclusive'} as any)[tier]||'All Packages';
              const getAvailable=(vendor:string,instant:boolean):any[]=>{
                if(!vendor) return [];
                if(vendor==='Gunabh'){
                  const cat=instant?'Instant':'All Packages';
                  const all=PP_VENDORS.filter((v:any)=>v.vendor==='Gunabh'&&v.category===cat);
                  const needed=new Set<string>();
                  if(!isFreeForm){
                    excelDelivs.forEach(ex=>{(EXCEL_TO_VENDOR[ex]?.Gunabh||[]).forEach((n:string)=>needed.add(n));});
                    if(needed.has('Full Event Video (per hr) Single Cam')) needed.add('Full Event Video (per hr) Double Cam');
                  }
                  const delivNames=isFreeForm?all.map((v:any)=>v.deliverable):Array.from(needed);
                  const seen=new Set<string>();
                  return delivNames.filter((n:string)=>{if(seen.has(n))return false;seen.add(n);return true;}).map((name:string)=>{
                    const entry=all.find((v:any)=>v.deliverable===name);
                    return {deliverable:name,rate:entry?((entry.rates as any)[serviceKey]||0):0,hasRate:!!(entry&&(entry.rates as any)[serviceKey]>0),isSingleCam:name==='Full Event Video (per hr) Single Cam',isDoubleCam:name==='Full Event Video (per hr) Double Cam'};
                  });
                }
                const allowed=new Set(SHANKAR_TIER_CATS[tier]||['All Packages','Extras']);
                const all=PP_VENDORS.filter((v:any)=>v.vendor==='Shankar'&&allowed.has(v.category)&&((v.rates as any)[serviceKey]||0)>0);
                const byName=new Map<string,any>();
                all.forEach((v:any)=>{const ex=byName.get(v.deliverable);if(!ex){byName.set(v.deliverable,v);return;}const s1=ex.category==='All Packages'||ex.category==='Extras'?0:1;const s2=v.category==='All Packages'||v.category==='Extras'?0:1;if(s2>s1)byName.set(v.deliverable,v);});
                const entries=Array.from(byName.values());
                if(isFreeForm) return entries.map((v:any)=>({deliverable:v.deliverable,rate:(v.rates as any)[serviceKey]||0,hasRate:true}));
                const needed=new Set<string>();
                excelDelivs.forEach(ex=>{(EXCEL_TO_VENDOR[ex]?.Shankar||[]).forEach((n:string)=>needed.add(n));});
                return entries.filter((v:any)=>needed.has(v.deliverable)).map((v:any)=>({deliverable:v.deliverable,rate:(v.rates as any)[serviceKey]||0,hasRate:true}));
              };
              const available=getAvailable(curVendor,isInstant);
              const handleVendorSelect=(vendor:string)=>{
                const same=vendor===curVendor;
                setSelectedPPVendor((prev:any)=>({...prev,[pkg.id]:same?'':vendor}));
                if(!same){
                  const avail=getAvailable(vendor,isInstant);
                  const days=Math.max(1,(pkg.eventDates||[]).filter((d:any)=>d.date).length);
                  // Rule: if 2+ video cameras (Traditional Video + Cinematography), default Double Cam
                  // pkg.contractors is empty during wizard — check CONTRACTORS data directly
                  const selectedContractor=pkg.contractor;
                  const liveDelivs=selectedContractor?CONTRACTORS.filter((c:any)=>
                    c.contractor===selectedContractor&&c.status==='Active'&&
                    ((c.rates as any)[serviceKey]!==undefined)
                  ).map((c:any)=>c.deliverable.toLowerCase()):[];
                  const hasTwoVideoCams=liveDelivs.some((d:string)=>d.includes('traditional video'))
                    &&liveDelivs.some((d:string)=>d.includes('cinematography'));
                  const auto:SelectedPPVendor[]=avail
                    .filter((a:any)=>hasTwoVideoCams?!a.isSingleCam:!a.isDoubleCam)
                    .map((a:any)=>({vendor,deliverable:a.deliverable,rate:a.rate,qty:a.deliverable.toLowerCase().includes('per hr')?days:1}));
                  setPkgPPVendors((prev:any)=>({...prev,[pkg.id]:auto}));
                } else { setPkgPPVendors((prev:any)=>({...prev,[pkg.id]:[]})); }
              };
              const handleInstantToggle=(instant:boolean)=>{
                setIsInstantDelivery((prev:any)=>({...prev,[pkg.id]:instant}));
                if(curVendor==='Gunabh'&&curSels.length>0){
                  const newAvail=getAvailable('Gunabh',instant);
                  const updated=curSels.map((sel:any)=>{const found=newAvail.find((a:any)=>a.deliverable===sel.deliverable);return found?{...sel,rate:found.rate}:sel;});
                  setPkgPPVendors((prev:any)=>({...prev,[pkg.id]:updated}));
                }
              };
              const swapCam=(toDouble:boolean)=>{
                const days=Math.max(1,(pkg.eventDates||[]).filter((d:any)=>d.date).length);
                const fromName=toDouble?'Full Event Video (per hr) Single Cam':'Full Event Video (per hr) Double Cam';
                const toName=toDouble?'Full Event Video (per hr) Double Cam':'Full Event Video (per hr) Single Cam';
                const toEntry=available.find((a:any)=>a.deliverable===toName);
                if(!toEntry) return;
                setPkgPPVendors((prev:any)=>({...prev,[pkg.id]:curSels.map((s:any)=>s.deliverable===fromName?{...s,deliverable:toName,rate:toEntry.rate,qty:days}:s)}));
              };
              const upsertSel=(deliverable:string,rate:number,qty:number)=>{
                const exists=curSels.find((s:any)=>s.deliverable===deliverable);
                if(exists){setPkgPPVendors((prev:any)=>({...prev,[pkg.id]:curSels.map((s:any)=>s.deliverable===deliverable?{...s,rate,qty}:s)}));}
                else{setPkgPPVendors((prev:any)=>({...prev,[pkg.id]:[...curSels,{vendor:curVendor,deliverable,rate,qty}]}));}
              };
              const removeSel=(deliverable:string)=>setPkgPPVendors((prev:any)=>({...prev,[pkg.id]:curSels.filter((s:any)=>s.deliverable!==deliverable)}));
              const isSelected=(name:string)=>curSels.some((s:any)=>s.deliverable===name);
              const getSel=(name:string)=>curSels.find((s:any)=>s.deliverable===name);
              return (
                <div key={pi} className="card" style={{marginBottom:14}}>
                  <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:14}}>
                    <span className={`badge ${tier==='Best'?'badge-best':tier==='Luxe'?'badge-luxe':'badge-elite'}`}>{tier}</span>
                    <span style={{fontSize:14,fontWeight:600}}>{pkg.product.productName}</span>
                    {ppTotal>0&&<span style={{marginLeft:'auto',fontSize:13,fontWeight:700,color:'var(--teal)'}}>PP: {formatCurrency(ppTotal)}</span>}
                  </div>
                  <label style={{fontSize:12,marginBottom:6}}>Select PP Vendor</label>
                  <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:12}}>
                    {ppVendorNames.map((name:any)=>(<button key={name} onClick={()=>handleVendorSelect(name)} style={{padding:'8px 18px',borderRadius:6,fontSize:13,fontWeight:curVendor===name?700:400,cursor:'pointer',fontFamily:'inherit',background:curVendor===name?'var(--teal)':'var(--bg-subtle)',color:curVendor===name?'#fff':'var(--text-primary)',border:`1.5px solid ${curVendor===name?'var(--teal)':'var(--border)'}`}}>{name}</button>))}
                    <button onClick={()=>{setSelectedPPVendor((p:any)=>({...p,[pkg.id]:''}));setPkgPPVendors((p:any)=>({...p,[pkg.id]:[]}));}} style={{padding:'8px 18px',borderRadius:6,fontSize:13,cursor:'pointer',fontFamily:'inherit',background:'transparent',color:'var(--text-muted)',border:'1.5px solid var(--border)'}}>No PP Vendor</button>
                  </div>
                  {curVendor==='Gunabh'&&(<div style={{marginBottom:12,padding:'8px 14px',background:'#FFFBF0',border:'1.5px solid #F6E05E',borderRadius:6,display:'flex',alignItems:'center',gap:10}}>
                    <span style={{fontSize:12,fontWeight:600,color:'#B7791F'}}>⚡ Delivery:</span>
                    {['Standard','Instant'].map(mode=>{const active=mode==='Instant'?isInstant:!isInstant;return(<button key={mode} onClick={()=>handleInstantToggle(mode==='Instant')} style={{padding:'4px 14px',borderRadius:100,fontSize:12,cursor:'pointer',fontFamily:'inherit',fontWeight:active?700:400,background:active?'var(--gold)':'transparent',color:active?'#fff':'var(--text-muted)',border:`1px solid ${active?'var(--gold)':'var(--border)'}`}}>{mode}{mode==='Instant'?' ⚡':''}</button>);})}
                    <span style={{fontSize:11,color:'var(--text-muted)'}}>Instant = faster turnaround, higher rates</span>
                  </div>)}
                  {curVendor&&available.length===0&&<div style={{fontSize:13,color:'var(--text-muted)',padding:12,background:'var(--bg-subtle)',borderRadius:6}}>No PP deliverables for {curVendor} on {serviceKey.replace(/_/g,' ')} service.</div>}
                  {curVendor&&available.length>0&&(<div>
                    <div style={{fontSize:12,color:'var(--text-muted)',marginBottom:8}}>{isFreeForm?'All available — remove any not needed:':'Auto-selected from your package:'}</div>
                    {available.map((avail:any)=>{
                      const sel=getSel(avail.deliverable);const active=!!sel;
                      if(avail.isDoubleCam&&!isSelected('Full Event Video (per hr) Double Cam')&&!isSelected('Full Event Video (per hr) Single Cam')) return null;
                      if(avail.isDoubleCam&&!active&&isSelected('Full Event Video (per hr) Single Cam')) return null;
                      return(<div key={avail.deliverable} style={{marginBottom:8,padding:'10px 14px',borderRadius:'var(--radius-sm)',border:`1.5px solid ${active?'var(--teal)':'var(--border)'}`,background:active?'rgba(56,178,172,0.06)':'#fff'}}>
                        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:active?8:0}}>
                          <div><div style={{fontSize:13,fontWeight:active?600:400,color:active?'var(--teal)':'var(--text-primary)'}}>{avail.deliverable}{!avail.hasRate&&<span style={{fontSize:11,color:'var(--amber)',marginLeft:6}}>⚠ Rate not on file — enter manually</span>}</div>{!active&&<div style={{fontSize:11,color:'var(--text-muted)'}}>₹{avail.rate.toLocaleString('en-IN')}{avail.rate===0?' (enter rate)':''}</div>}</div>
                          <div style={{display:'flex',gap:6,alignItems:'center'}}>
                            {active&&avail.isSingleCam&&<button onClick={()=>swapCam(true)} style={{fontSize:11,padding:'3px 10px',borderRadius:100,cursor:'pointer',fontFamily:'inherit',background:'#EBF8FF',color:'#2B6CB0',border:'1px solid #90CDF4'}}>Upgrade → Double Cam</button>}
                            {active&&avail.isDoubleCam&&<button onClick={()=>swapCam(false)} style={{fontSize:11,padding:'3px 10px',borderRadius:100,cursor:'pointer',fontFamily:'inherit',background:'#F0FFF4',color:'#276749',border:'1px solid #9AE6B4'}}>Switch → Single Cam</button>}
                            {active?<button onClick={()=>removeSel(avail.deliverable)} style={{fontSize:11,color:'var(--text-muted)',background:'transparent',border:'none',cursor:'pointer',padding:'2px 6px'}}>✕</button>:<button onClick={()=>upsertSel(avail.deliverable,avail.rate,avail.deliverable.toLowerCase().includes('per hr')?Math.max(1,(pkg.eventDates||[]).filter((d:any)=>d.date).length):1)} style={{padding:'4px 12px',borderRadius:100,fontSize:12,cursor:'pointer',fontFamily:'inherit',background:'var(--bg-subtle)',color:'var(--text-secondary)',border:'1px solid var(--border)'}}>+ Add</button>}
                          </div>
                        </div>
                        {active&&(<div style={{display:'flex',gap:8,alignItems:'center'}}>
                          <div style={{flex:1}}><label style={{fontSize:10}}>Rate (₹)</label><input type="number" className="input" style={{padding:'4px 8px',fontSize:12}} value={sel.rate} onChange={e=>upsertSel(avail.deliverable,Number(e.target.value),sel.qty||1)}/></div>
                          <div style={{width:70}}><label style={{fontSize:10}}>Qty</label><input type="number" min={1} className="input" style={{padding:'4px 8px',fontSize:12}} value={sel.qty||1} onChange={e=>upsertSel(avail.deliverable,sel.rate,Number(e.target.value))}/></div>
                          <div style={{textAlign:'right',flexShrink:0,minWidth:60}}><div style={{fontSize:10,color:'var(--text-muted)'}}>Total</div><div style={{fontSize:13,fontWeight:700,color:'var(--teal)'}}>{formatCurrency(sel.rate*(sel.qty||1))}</div></div>
                        </div>)}
                      </div>);
                    })}
                    {/* ── Photo Editing Charge ── */}
                    <div style={{marginTop:12,padding:'12px 14px',background:'#F0F9FF',border:'1.5px solid #BAE6FD',borderRadius:'var(--radius-sm)'}}>
                      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:pe.enabled?10:0}}>
                        <div>
                          <span style={{fontSize:13,fontWeight:600,color:'#0369A1'}}>📸 Photo Editing Charge</span>
                          <span style={{fontSize:11,color:'var(--text-muted)',marginLeft:8}}>₹50/photo standard rate</span>
                        </div>
                        <button onClick={()=>setPhotoEditCharge((prev:any)=>({...prev,[pkg.id]:{...pe,enabled:!pe.enabled,rate:pe.rate||50,qty:pe.qty||0}}))}
                          style={{padding:'4px 12px',borderRadius:100,fontSize:12,cursor:'pointer',fontFamily:'inherit',
                            background:pe.enabled?'#0369A1':'var(--bg-subtle)',color:pe.enabled?'#fff':'var(--text-secondary)',
                            border:`1px solid ${pe.enabled?'#0369A1':'var(--border)'}`}}>
                          {pe.enabled?'✓ Added':'+ Add'}
                        </button>
                      </div>
                      {pe.enabled&&(<div style={{display:'flex',gap:8,alignItems:'center'}}>
                        <div style={{flex:1}}><label style={{fontSize:10}}>Rate per photo (₹)</label><input type="number" min={1} className="input" style={{padding:'4px 8px',fontSize:12}} value={pe.rate||50} onChange={e=>setPhotoEditCharge((prev:any)=>({...prev,[pkg.id]:{...pe,rate:Number(e.target.value)}}))} /></div>
                        <div style={{width:90}}><label style={{fontSize:10}}>No. of photos</label><input type="number" min={0} className="input" style={{padding:'4px 8px',fontSize:12}} value={pe.qty||0} onChange={e=>setPhotoEditCharge((prev:any)=>({...prev,[pkg.id]:{...pe,qty:Number(e.target.value)}}))} /></div>
                        <div style={{textAlign:'right',flexShrink:0,minWidth:70}}><div style={{fontSize:10,color:'var(--text-muted)'}}>Total</div><div style={{fontSize:13,fontWeight:700,color:'#0369A1'}}>{formatCurrency((pe.rate||50)*(pe.qty||0))}</div></div>
                      </div>)}
                    </div>
                    {(curSels.length>0||pe.enabled)&&(<div style={{marginTop:8,padding:'8px 14px',background:'rgba(56,178,172,0.08)',borderRadius:6,display:'flex',justifyContent:'space-between'}}>
                      <span style={{fontSize:13,fontWeight:700,color:'var(--teal)'}}>PP Total: {formatCurrency(ppTotal)}</span>
                      <span style={{fontSize:12,color:'var(--text-muted)'}}>{curSels.length} deliverable(s){pe.enabled?` + photo editing (${pe.qty||0} photos)`:''}</span>
                    </div>)}
                  </div>)}
                </div>
              );
            })}
            <div style={{display:'flex',justifyContent:'space-between',marginTop:8}}>
              <button className="btn btn-secondary" onClick={()=>setStep(4)}><ChevronLeft size={14}/> Back</button>
              <button className="btn btn-primary btn-lg" onClick={()=>setStep(6)}>Continue <ChevronRight size={15}/></button>
            </div>
          </div>
        );
      })()}

            {/* ── STEP 5: Calendar Mapping ─────────────────────── */}
      {step === 6 && (
        <div>
          <h2 className="gold-accent" style={{ marginBottom:6 }}>Map Packages to Dates</h2>
          <p style={{ color:'var(--text-secondary)', fontSize:13, marginBottom:16 }}>
            Each package shows which dates it covers. Toggle dates on/off per package. Calendar view shows the full picture.
          </p>

          {/* Calendar view */}
          {(() => {
            const allDates: {date:string;label:string}[] = [];
            packages.forEach(pkg => (pkg.eventDates||[]).forEach(ed => {
              if (ed.date && !allDates.find(d=>d.date===ed.date)) allDates.push(ed);
            }));
            allDates.sort((a,b)=>a.date.localeCompare(b.date));
            if (!allDates.length) return null;
            return (
              <div style={{ overflowX:'auto', marginBottom:20 }}>
                <div style={{ display:'flex', gap:10, minWidth: allDates.length * 180 + 'px' }}>
                  {allDates.map((ed,di) => {
                    const pkgsOnDay = packages.filter(p=>(p.eventDates||[]).some(d=>d.date===ed.date));
                    return (
                      <div key={di} style={{ flex:'0 0 170px' }}>
                        <div style={{ padding:'8px 12px', background:'var(--teal)', color:'#fff', borderRadius:'var(--radius-md) var(--radius-md) 0 0', textAlign:'center' }}>
                          <div style={{ fontSize:13, fontWeight:700 }}>
                            {new Date(ed.date).toLocaleDateString('en-IN',{weekday:'short',day:'2-digit',month:'short'})}
                          </div>
                          <div style={{ fontSize:11, opacity:0.75 }}>{ed.label}</div>
                        </div>
                        <div style={{ border:'1px solid var(--border)', borderTop:'none', borderRadius:'0 0 var(--radius-md) var(--radius-md)', minHeight:120, padding:6, background:'#F0FFF4' }}>
                          {pkgsOnDay.length===0 && (
                            <div style={{ textAlign:'center', padding:'20px 0', color:'var(--text-muted)', fontSize:11 }}>Nothing assigned</div>
                          )}
                          {pkgsOnDay.map((pkg,pi) => (
                            <div key={pi} style={{ padding:'6px 8px', background:'#fff', border:`1.5px solid ${pkg.product.tier==='Elite'?'#D6BCFA':pkg.product.tier==='Luxe'?'#90CDF4':'#9AE6B4'}`, borderRadius:6, marginBottom:4 }}>
                              <div style={{ fontSize:10, fontWeight:700, color:pkg.product.tier==='Elite'?'#553C9A':pkg.product.tier==='Luxe'?'#2C5282':'#276749' }}>{pkg.product.tier} · {pkg.packageType}</div>
                              <div style={{ fontSize:11, fontWeight:600, color:'var(--text-primary)' }}>{pkg.product.productName}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Assignment controls */}
          <div className="card">
            <h4 style={{ marginBottom:14 }}>Assign Packages to Dates</h4>
            <p style={{ fontSize:12, color:'var(--text-muted)', marginBottom:14 }}>Tap date chips to assign/unassign each package to specific dates.</p>
            {packages.map((pkg, pi) => {
              const allDates2: {date:string;label:string}[] = [];
              packages.forEach(p => (p.eventDates||[]).forEach(ed => {
                if (ed.date && !allDates2.find(d=>d.date===ed.date)) allDates2.push(ed);
              }));
              allDates2.sort((a,b)=>a.date.localeCompare(b.date));
              return (
                <div key={pi} style={{ padding:'12px 0', borderBottom: pi<packages.length-1?'1px solid var(--border)':'none' }}>
                  <div style={{ display:'flex', gap:6, alignItems:'center', marginBottom:8 }}>
                    <span className={`badge ${pkg.product.tier==='Best'?'badge-best':pkg.product.tier==='Luxe'?'badge-luxe':'badge-elite'}`}>{pkg.product.tier}</span>
                    <span style={{ fontSize:13, fontWeight:600 }}>{pkg.product.productName}</span>
                    <span className="badge badge-gold" style={{ fontSize:10 }}>{pkg.packageType}</span>
                  </div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                    {allDates2.map((ed,di) => {
                      const isOn = (pkg.eventDates||[]).some(d=>d.date===ed.date);
                      return (
                        <button key={di} onClick={() => {
                          const cur = pkg.eventDates||[];
                          const updated = isOn ? cur.filter(d=>d.date!==ed.date) : [...cur, ed];
                          updatePackage(pkg.id, { eventDates: updated });
                        }}
                          style={{ padding:'5px 12px', borderRadius:100, fontSize:12, cursor:'pointer', fontFamily:'inherit', fontWeight:isOn?600:400, background:isOn?'var(--teal)':'#fff', color:isOn?'#fff':'var(--text-secondary)', border:`1.5px solid ${isOn?'var(--teal)':'var(--border)'}`, transition:'all 0.12s' }}>
                          {isOn ? '✓ ' : '+ '}
                          {new Date(ed.date).toLocaleDateString('en-IN',{day:'2-digit',month:'short'})} · {ed.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ display:'flex', justifyContent:'space-between', marginTop:20 }}>
            <button className="btn btn-secondary" onClick={()=>setStep(4)}><ChevronLeft size={14}/> Back</button>
            <button className="btn btn-primary btn-lg" onClick={()=>setStep(7)}>
              Continue <ChevronRight size={15}/>
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 6: Album ──────────────────────────────────────── */}
      {step === 7 && (
        <div>
          <h2 className="gold-accent" style={{ marginBottom:6 }}>Album Selection</h2>
          <p style={{ color:'var(--text-secondary)', fontSize:13, marginBottom:20 }}>
            Fixed packages show 3 pre-selected options. Customer picks one.
          </p>
          {packages.map(pkg => {
            if (!pkg.product.hasAlbum) {
              return (
                <div key={pkg.id} className="card" style={{ marginBottom:14 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <span style={{ fontWeight:600 }}>{pkg.product.productName}</span>
                    <span className="badge badge-amber">No Album</span>
                  </div>
                  <p style={{ color:'var(--text-muted)', fontSize:13, marginTop:8 }}>This is a video-only package. No album included.</p>
                </div>
              );
            }

            const isNowSemiFixed = pkg.packageType === 'Semi-Fixed' || pkg.packageType === 'Fully Personalized';
            const fixedOpts = !isNowSemiFixed ? FIXED_ALBUM_OPTIONS[pkg.product.code] : null;

            // SEMI-FIXED / FP — full album selector
            if (isNowSemiFixed) {
              const vendorList = PAPER_OPTIONS.map(p => p.vendorName).filter((v,i,a) => a.indexOf(v)===i);
              const sfState = semiFixedAlbumState[pkg.id] || {};
              const filteredPapers = PAPER_OPTIONS.filter(p => p.vendorName === sfState.vendorName);
              const filteredCovers = COVER_OPTIONS.filter(c => c.vendorName === sfState.vendorName);
              const sizes = filteredPapers.map(p => p.size).filter((v,i,a) => a.indexOf(v)===i);
              const selPaper = PAPER_OPTIONS.find(p => p.paperCode === sfState.paperCode);
              const selCover = COVER_OPTIONS.find(c => c.coverCode === sfState.coverCode);
              const sheets = sfState.sheets || 20;
              const numAlbums = pkg.product.numAlbums || 1;
              const designCharge = sfState.designChargeTier === 'Elite' ? 1500 : sfState.designChargeTier === 'Premium' ? 1000 : 500;
              const albumCost = selPaper && selCover ? calculateAlbumCost({ paperRatePerSheet: selPaper.ratePerSheet, coverRate: selCover.rate, sheets, numAlbums, designChargeAmount: designCharge }) : 0;

              return (
                <div key={pkg.id} className="card" style={{ marginBottom:14, border:'1.5px solid var(--amber)' }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8, marginBottom:4 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <span className="badge badge-amber">Semi-Fixed</span>
                      <span style={{ fontWeight:600, fontSize:14 }}>{pkg.product.serviceName} — {pkg.product.productName}</span>
                    </div>
                    <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'var(--text-secondary)', cursor:'pointer', flexShrink:0 }}>
                      <input type="checkbox" checked={!!pkg.noAlbum}
                        onChange={e => updatePackage(pkg.id, e.target.checked
                          ? { noAlbum: true, album: undefined, albumOptionIndex: undefined }
                          : { noAlbum: false })} />
                      No album needed
                    </label>
                  </div>
                  {pkg.noAlbum ? (
                    <div style={{ padding:'10px 14px', background:'#F7FAFC', border:'1px solid var(--border)', borderRadius:6, fontSize:13, color:'var(--text-secondary)' }}>
                      No album — this package's quote excludes an album entirely.
                    </div>
                  ) : (
                  <>
                  <div style={{ fontSize:12, color:'var(--amber)', marginBottom:14 }}>
                    Switched from Fixed — select album freely from full catalog
                  </div>
                  <div className="form-row" style={{ marginBottom:12 }}>
                    <div className="form-group" style={{ marginBottom:0 }}>
                      <label>Vendor</label>
                      <select className="select" value={sfState.vendorName||''} onChange={e => setSemiFixedAlbumState(prev => ({ ...prev, [pkg.id]: { ...sfState, vendorName: e.target.value, paperCode:'', coverCode:'' } }))}>
                        <option value="">Select vendor</option>
                        {vendorList.map(v => <option key={v}>{v}</option>)}
                      </select>
                    </div>
                    <div className="form-group" style={{ marginBottom:0 }}>
                      <label>Paper Type</label>
                      <select className="select" value={sfState.paperCode||''} onChange={e => setSemiFixedAlbumState(prev => ({ ...prev, [pkg.id]: { ...sfState, paperCode: e.target.value } }))}>
                        <option value="">Select paper</option>
                        {sizes.map(size => (
                          <optgroup key={size} label={`Size ${size}`}>
                            {filteredPapers.filter(p => p.size === size).map(p => (
                              <option key={p.paperCode} value={p.paperCode}>{p.paperType} — ₹{p.ratePerSheet}/sheet</option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="form-row" style={{ marginBottom:12 }}>
                    <div className="form-group" style={{ marginBottom:0 }}>
                      <label>Cover Type</label>
                      <select className="select" value={sfState.coverCode||''} onChange={e => setSemiFixedAlbumState(prev => ({ ...prev, [pkg.id]: { ...sfState, coverCode: e.target.value } }))}>
                        <option value="">Select cover</option>
                        {filteredCovers.map(c => <option key={c.coverCode} value={c.coverCode}>{c.coverType} — ₹{c.rate}</option>)}
                      </select>
                    </div>
                    <div className="form-group" style={{ marginBottom:0 }}>
                      <label>Sheets</label>
                      <input type="number" className="input" min={1} value={sheets}
                        onChange={e => setSemiFixedAlbumState(prev => ({ ...prev, [pkg.id]: { ...sfState, sheets: Number(e.target.value) } }))} />
                    </div>
                  </div>
                  <div className="form-group" style={{ marginBottom:12 }}>
                    <label>Design Charge Tier</label>
                    <div style={{ display:'flex', gap:8 }}>
                      {(['Standard','Premium','Elite'] as const).map(tier => (
                        <button key={tier} onClick={() => setSemiFixedAlbumState(prev => ({ ...prev, [pkg.id]: { ...sfState, designChargeTier: tier } }))}
                          style={{ flex:1, padding:'8px', borderRadius:'var(--radius-sm)', cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight:600, background: sfState.designChargeTier===tier?'var(--gold-bg)':'#fff', border:`1.5px solid ${sfState.designChargeTier===tier?'var(--gold)':'var(--border)'}`, color: sfState.designChargeTier===tier?'var(--gold)':'var(--text-secondary)' }}>
                          {tier}<div style={{ fontSize:11, fontWeight:400 }}>₹{tier==='Standard'?500:tier==='Premium'?1000:1500}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  {selPaper && selCover && (
                    <div style={{ marginBottom:12 }}>
                      <button className="btn btn-teal" onClick={() => {
                        const album: SelectedAlbum = {
                          vendorId: selPaper.vendorId, vendorName: selPaper.vendorName,
                          size: selPaper.size, paperType: selPaper.paperType,
                          paperCode: selPaper.paperCode, paperRatePerSheet: selPaper.ratePerSheet,
                          coverType: selCover.coverType, coverCode: selCover.coverCode,
                          coverRate: selCover.rate, sheets, numAlbums,
                          designChargeTier: sfState.designChargeTier || 'Standard',
                          designChargeAmount: designCharge, totalAlbumCost: albumCost,
                        };
                        updatePackage(pkg.id, { album });
                      }}>
                        ✓ Confirm Album — {formatCurrency(albumCost)}
                      </button>
                    </div>
                  )}
                  {pkg.album && (
                    <div style={{ padding:'10px 14px', background:'var(--teal-bg)', border:'1.5px solid var(--teal)', borderRadius:8, fontSize:13, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                      <span>✓ <strong>{pkg.album.vendorName}</strong> · {pkg.album.paperType} · {pkg.album.sheets} sheets</span>
                      <span style={{ fontWeight:700, color:'var(--teal)', fontSize:15 }}>{formatCurrency(pkg.album.totalAlbumCost)}</span>
                    </div>
                  )}
                  {/* Back to Fixed option */}
                  <div style={{ marginTop:12, display:'flex', alignItems:'center', gap:10 }}>
                    <button onClick={() => {
                        const origCode = pkg.originalProductCode || pkg.product.code.replace('-SEMI-','-PKG-');
                        const origProduct = getProductByCode(origCode) || { ...pkg.product, packageType: 'Fixed', code: origCode };
                        // Auto-select first album option so Continue isn't blocked after switching back
                        const restoredOpts = FIXED_ALBUM_OPTIONS[(origProduct as any).code || origCode];
                        const autoAlbum = restoredOpts?.[0] || undefined;
                        updatePackage(pkg.id, { packageType: 'Fixed',
                          album: autoAlbum, albumOptionIndex: autoAlbum ? 0 : undefined,
                          product: origProduct as any, originalProductCode: origCode
                        });
                        setSemiFixedAlbumState(prev => { const n = {...prev}; delete n[pkg.id]; return n; });
                      }}
                      style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'6px 12px', borderRadius:'var(--radius-sm)', background:'transparent', border:'1px solid var(--border)', color:'var(--text-secondary)', cursor:'pointer', fontSize:12, fontFamily:'inherit' }}>
                      ← Back to Fixed Album Options
                    </button>
                  </div>
                  </>
                  )}
                </div>
              );
            }

            // FIXED — show 3 pre-selected options
            if (!fixedOpts) {
              return (
                <div key={pkg.id} className="card" style={{ marginBottom:14 }}>
                  <p style={{ color:'var(--text-muted)', fontSize:13 }}>No fixed album options found for {pkg.product.code}. Please switch to Semi-Fixed.</p>
                </div>
              );
            }

            return (
              <div key={pkg.id} className="card" style={{ marginBottom:14 }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8, marginBottom:4 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <BookOpen size={14} color="var(--gold)" />
                    <span style={{ fontWeight:600, fontSize:14 }}>{pkg.product.serviceName} — {pkg.product.productName}</span>
                  </div>
                  <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'var(--text-secondary)', cursor:'pointer', flexShrink:0 }}>
                    <input type="checkbox" checked={!!pkg.noAlbum}
                      onChange={e => updatePackage(pkg.id, e.target.checked
                        ? { noAlbum: true, album: undefined, albumOptionIndex: undefined }
                        : { noAlbum: false })} />
                    No album needed
                  </label>
                </div>
                {pkg.noAlbum ? (
                  <div style={{ padding:'10px 14px', background:'#F7FAFC', border:'1px solid var(--border)', borderRadius:6, fontSize:13, color:'var(--text-secondary)' }}>
                    No album — this package's quote excludes an album entirely.
                  </div>
                ) : (
                <>
                <div style={{ fontSize:12, color:'var(--text-muted)', marginBottom:14 }}>
                  Base: {fixedOpts.baseSheets} sheets · {pkg.product.numAlbums} album(s) · Design charge: {formatCurrency(fixedOpts.designChargeAmount)}
                </div>
                {/* Multi-day logic: 1-4 days = base album, 5+ days = extra charge */}
                {(() => {
                  const days = Math.max(1, pkg.eventDates.filter(d=>d.date).length);
                  const extraDays = Math.max(0, days - 4);
                  const ec = extraCharges[pkg.id] || { mode: 'sheets', value: 0 };

                  if (days <= 4) {
                    return days > 1 ? (
                      <div style={{ marginBottom:14, padding:'10px 14px', background:'#F0FFF4', border:'1px solid #9AE6B4', borderRadius:'var(--radius-sm)', fontSize:13 }}>
                        <strong>📅 {days}-day event</strong> — covered within base package. Standard album applies.
                      </div>
                    ) : null;
                  }

                  // 5+ days — show extra charge options
                  const selectedAlbumPaperRate = pkg.album ? pkg.album.paperRatePerSheet : (fixedOpts.options[0]?.paperRatePerSheet || 0);
                  const extraSheetsPerDay = fixedOpts.baseSheets; // 1 day = 1x base sheets
                  const extraSheetsCost = ec.mode === 'sheets'
                    ? (ec.value || extraDays * Math.round(extraSheetsPerDay / 4)) * selectedAlbumPaperRate * (pkg.product.numAlbums || 1)
                    : 0;
                  const extraFlatCost = ec.mode === 'flat' ? (ec.value || 0) : 0;
                  const extraTotal = ec.mode === 'sheets' ? extraSheetsCost : extraFlatCost;

                  return (
                    <div style={{ marginBottom:14, padding:'14px 16px', background:'#FFF3E0', border:'1.5px solid #F6AD55', borderRadius:'var(--radius-md)' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
                        <span style={{ fontSize:15 }}>⚠️</span>
                        <strong style={{ fontSize:13, color:'var(--amber)' }}>{days}-day event — {extraDays} day(s) beyond 4-day package limit</strong>
                      </div>
                      <p style={{ fontSize:12, color:'var(--text-secondary)', marginBottom:12 }}>
                        Base package covers 1–4 days. Extra days need additional album charges. Choose how to bill:
                      </p>
                      <div style={{ display:'flex', gap:8, marginBottom:12 }}>
                        <button onClick={() => setExtraCharges(prev => ({ ...prev, [pkg.id]: { ...ec, mode: 'sheets' } }))}
                          style={{ flex:1, padding:'9px', borderRadius:'var(--radius-sm)', border:`1.5px solid ${ec.mode==='sheets'?'var(--amber)':'var(--border)'}`, background: ec.mode==='sheets'?'#FFFAF0':'#fff', cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight: ec.mode==='sheets'?600:400, color: ec.mode==='sheets'?'var(--amber)':'var(--text-secondary)' }}>
                          📄 Extra Sheets
                        </button>
                        <button onClick={() => setExtraCharges(prev => ({ ...prev, [pkg.id]: { ...ec, mode: 'flat' } }))}
                          style={{ flex:1, padding:'9px', borderRadius:'var(--radius-sm)', border:`1.5px solid ${ec.mode==='flat'?'var(--amber)':'var(--border)'}`, background: ec.mode==='flat'?'#FFFAF0':'#fff', cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight: ec.mode==='flat'?600:400, color: ec.mode==='flat'?'var(--amber)':'var(--text-secondary)' }}>
                          💰 Flat Extra Charge
                        </button>
                      </div>
                      {ec.mode === 'sheets' && (
                        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                          <div style={{ flex:1 }}>
                            <label style={{ textTransform:'none', fontSize:12 }}>Extra sheets to add (for {extraDays} extra day(s))</label>
                            <input type="number" className="input" min={0} placeholder={`e.g. ${extraDays * Math.round(extraSheetsPerDay/4)}`}
                              value={ec.value || ''}
                              onChange={e => setExtraCharges(prev => ({ ...prev, [pkg.id]: { mode:'sheets', value: Number(e.target.value) } }))} />
                          </div>
                          {ec.value > 0 && pkg.album && (
                            <div style={{ textAlign:'right', flexShrink:0 }}>
                              <div style={{ fontSize:11, color:'var(--text-muted)' }}>{ec.value} sheets × ₹{selectedAlbumPaperRate}/sheet</div>
                              <div style={{ fontSize:15, fontWeight:700, color:'var(--amber)' }}>+{formatCurrency(extraSheetsCost)}</div>
                            </div>
                          )}
                        </div>
                      )}
                      {ec.mode === 'flat' && (
                        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                          <div style={{ flex:1 }}>
                            <label style={{ textTransform:'none', fontSize:12 }}>Flat extra charge amount (₹)</label>
                            <input type="number" className="input" min={0} placeholder="e.g. 5000"
                              value={ec.value || ''}
                              onChange={e => setExtraCharges(prev => ({ ...prev, [pkg.id]: { mode:'flat', value: Number(e.target.value) } }))} />
                          </div>
                          {ec.value > 0 && (
                            <div style={{ textAlign:'right', flexShrink:0 }}>
                              <div style={{ fontSize:15, fontWeight:700, color:'var(--amber)' }}>+{formatCurrency(extraFlatCost)}</div>
                            </div>
                          )}
                        </div>
                      )}
                      {extraTotal > 0 && (
                        <div style={{ marginTop:10, padding:'8px 12px', background:'rgba(214,133,10,0.1)', borderRadius:6, fontSize:13, display:'flex', justifyContent:'space-between' }}>
                          <span style={{ color:'var(--amber)' }}>Extra day charge added to quote</span>
                          <strong style={{ color:'var(--amber)' }}>+{formatCurrency(extraTotal)}</strong>
                        </div>
                      )}
                    </div>
                  );
                })()}

                <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:14 }}>
                  {fixedOpts.options.map((opt, oi) => {
                    if (!opt) return null;
                    const cost = calculateAlbumCost({
                      paperRatePerSheet: opt.paperRatePerSheet, coverRate: opt.coverRate,
                      sheets: fixedOpts.baseSheets, numAlbums: pkg.product.numAlbums,
                      designChargeAmount: fixedOpts.designChargeAmount,
                    });
                    const isSel = pkg.albumOptionIndex === oi;
                    return (
                      <button key={oi} onClick={() => {
                        const album: SelectedAlbum = {
                          ...opt,
                          sheets: fixedOpts.baseSheets,
                          numAlbums: pkg.product.numAlbums,
                          designChargeTier: fixedOpts.designChargeTier,
                          designChargeAmount: fixedOpts.designChargeAmount,
                          totalAlbumCost: cost,
                        };
                        updatePackage(pkg.id, { album, albumOptionIndex: oi });
                      }}
                        style={{ padding:14, borderRadius:'var(--radius-md)', cursor:'pointer', textAlign:'left', background: isSel?'var(--gold-bg)':'#F7FAFC', border:`1.5px solid ${isSel?'var(--gold)':'var(--border)'}`, fontFamily:'inherit', transition:'all 0.12s', boxShadow: isSel?'0 0 0 3px rgba(183,121,31,0.1)':'none' }}>
                        <div style={{ fontSize:11, color:'var(--text-muted)', marginBottom:4 }}>Option {oi+1}</div>
                        <div style={{ fontSize:13, fontWeight:700, color: isSel?'var(--gold)':'var(--text-primary)', marginBottom:6 }}>{opt.vendorName}</div>
                        <div style={{ fontSize:11, color:'var(--text-secondary)', marginBottom:2 }}>📄 {opt.paperType}</div>
                        <div style={{ fontSize:11, color:'var(--text-secondary)', marginBottom:8 }}>📦 {opt.coverType.length > 40 ? opt.coverType.substring(0,40)+'…' : opt.coverType}</div>
                        <div style={{ fontSize:15, fontWeight:700, color: isSel?'var(--gold)':'var(--text-primary)' }}>{formatCurrency(cost)}</div>
                      </button>
                    );
                  })}
                </div>

                {pkg.album && (
                  <div style={{ padding:'10px 14px', background:'var(--teal-bg)', border:'1px solid #9AE6B4', borderRadius:6, fontSize:13 }}>
                    ✓ Selected: <strong>{pkg.album.vendorName}</strong> — {formatCurrency(pkg.album.totalAlbumCost)}
                  </div>
                )}

                {/* ── Album Adjustments + Custom Sheet Rate ── */}
                {pkg.album && (
                  <div style={{marginTop:12,padding:'14px 16px',background:'#FFFBF0',border:'1.5px solid #F6E05E',borderRadius:'var(--radius-sm)'}}>
                    <div style={{fontSize:12,fontWeight:700,color:'#B7791F',marginBottom:12}}>📄 Album Adjustments</div>

                    {/* 1. Design charge per sheet */}
                    <div style={{marginBottom:14,padding:'10px 12px',background:'#FFF7ED',border:'1px solid #FED7AA',borderRadius:6}}>
                      <div style={{fontSize:12,fontWeight:600,color:'#92400E',marginBottom:6}}>
                        Design charge per sheet (₹)
                        <span style={{fontWeight:400,color:'var(--text-muted)',marginLeft:6,fontSize:11}}>Typically ₹45–₹60/sheet — vendor charges this on top of paper + cover</span>
                      </div>
                      <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                        <input type="number" className="input" min={0} max={100} placeholder="e.g. 50"
                          style={{width:100,padding:'4px 8px',fontSize:13}}
                          value={albumDesignChargePerSheet[pkg.id]||''}
                          onChange={e=>setAlbumDesignChargePerSheet((prev:any)=>({...prev,[pkg.id]:Number(e.target.value)||undefined}))} />
                        {(albumDesignChargePerSheet[pkg.id]||0)>0&&(()=>{
                          const dr=albumDesignChargePerSheet[pkg.id];
                          const bs=pkg.album.sheets||40;
                          const n=pkg.album.numAlbums||1;
                          return <div style={{fontSize:12,color:'#92400E'}}>
                            Primary design: {bs} sheets × ₹{dr} × {n} album(s) = <strong>+{formatCurrency(bs*dr*n)}</strong>
                          </div>;
                        })()}
                      </div>
                    </div>

                    {/* 2. Extra sheets */}
                    <div style={{marginBottom:12}}>
                      <label style={{textTransform:'none',fontSize:12,color:'var(--text-secondary)'}}>Extra sheets beyond base {pkg.album.sheets} sheets</label>
                      <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                        <input type="number" className="input" min={0} placeholder="e.g. 10"
                          style={{width:100,padding:'4px 8px',fontSize:13}}
                          value={extraAlbumSheets[pkg.id]||''}
                          onChange={e=>setExtraAlbumSheets((prev:any)=>({...prev,[pkg.id]:Math.max(0,Number(e.target.value))}))} />
                        {(extraAlbumSheets[pkg.id]||0)>0&&(()=>{
                          const es=extraAlbumSheets[pkg.id];
                          const n=pkg.album.numAlbums||1;
                          const paper=es*pkg.album.paperRatePerSheet*n;
                          const dr=albumDesignChargePerSheet[pkg.id]||0;
                          const design=dr>0?es*dr*n:0;
                          return <div style={{fontSize:12,color:'#B7791F'}}>
                            Paper: {es} × ₹{pkg.album.paperRatePerSheet} = +{formatCurrency(paper)}
                            {dr>0&&<span> · Design: {es} × ₹{dr} = +{formatCurrency(design)}</span>}
                          </div>;
                        })()}
                      </div>
                    </div>

                    {/* 3. Split into sets */}
                    <div style={{marginBottom:12}}>
                      <label style={{textTransform:'none',fontSize:12,color:'var(--text-secondary)'}}>Split total sheets into N sets (adds extra covers per extra set)</label>
                      <div style={{display:'flex',alignItems:'center',gap:10}}>
                        <input type="number" className="input" min={1} placeholder="e.g. 2"
                          style={{width:100,padding:'4px 8px',fontSize:13}}
                          value={albumSplitSets[pkg.id]||''}
                          onChange={e=>setAlbumSplitSets((prev:any)=>({...prev,[pkg.id]:Math.max(1,Number(e.target.value))}))} />
                        {(albumSplitSets[pkg.id]||1)>1&&(()=>{
                          const sp=albumSplitSets[pkg.id];
                          const ts=(pkg.album.sheets||40)+(extraAlbumSheets[pkg.id]||0);
                          const ec=(sp-1)*pkg.album.coverRate*(pkg.album.numAlbums||1);
                          return <div style={{fontSize:12,color:'#B7791F'}}>{sp} sets × {Math.round(ts/sp)} sheets · {sp-1} extra cover(s) = <strong>+{formatCurrency(ec)}</strong></div>;
                        })()}
                      </div>
                    </div>

                    {/* 4. Additional copies = full album total × N */}
                    <div>
                      <label style={{textTransform:'none',fontSize:12,color:'var(--text-secondary)'}}>Additional full copies (each = full album total)</label>
                      <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                        <input type="number" className="input" min={0} placeholder="e.g. 1"
                          style={{width:100,padding:'4px 8px',fontSize:13}}
                          value={albumAdditionalCopies[pkg.id]||''}
                          onChange={e=>setAlbumAdditionalCopies((prev:any)=>({...prev,[pkg.id]:Math.max(0,Number(e.target.value))}))} />
                        {(albumAdditionalCopies[pkg.id]||0)>0&&(()=>{
                          const cp=albumAdditionalCopies[pkg.id];
                          const dr=albumDesignChargePerSheet[pkg.id]||0;
                          const bs=pkg.album.sheets||40;
                          const es=extraAlbumSheets[pkg.id]||0;
                          const n=pkg.album.numAlbums||1;
                          const primaryDesign=dr*bs*n;
                          const extraPaper=es*pkg.album.paperRatePerSheet*n;
                          const extraDesign=dr>0?es*dr*n:0;
                          const oneCopy=pkg.album.totalAlbumCost+primaryDesign+extraPaper+extraDesign;
                          return <div style={{fontSize:12,color:'#B7791F'}}>
                            1 copy = {formatCurrency(pkg.album.totalAlbumCost)} album + {formatCurrency(primaryDesign)} design{(extraPaper+extraDesign)>0?` + ${formatCurrency(extraPaper+extraDesign)} extras`:''}
                            = <strong>{formatCurrency(oneCopy)}</strong>
                            &nbsp;× {cp} = <strong>+{formatCurrency(cp*oneCopy)}</strong>
                          </div>;
                        })()}
                      </div>
                    </div>
                  </div>
                )}

                <div style={{ marginTop:12, padding:'10px 14px', background:'#FFF8E1', border:'1px solid #FFE082', borderRadius:'var(--radius-sm)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <span style={{ fontSize:12, color:'#B8860B' }}>Customer wants a different album?</span>
                  <button onClick={() => {
                    // Switch to Semi-Fixed — carry all data, reset album only
                    const matchingSF = getMatchingSemiFixed(pkg.product);
                    const sfProduct = matchingSF[0] || pkg.product;
                    updatePackage(pkg.id, {
                      product: { ...sfProduct, packageType: 'Semi-Fixed' },
                      packageType: 'Semi-Fixed',
                      album: undefined,
                      albumOptionIndex: undefined,
                    });
                  }} style={{ padding:'5px 12px', borderRadius:'var(--radius-sm)', background:'#B8860B', color:'#fff', border:'none', fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', gap:5 }}>
                    <RefreshCw size={11} /> Switch to Semi-Fixed
                  </button>
                </div>
                </>
                )}
              </div>
            );
          })}
          <div style={{ display:'flex', justifyContent:'space-between', marginTop:8 }}>
            <button className="btn btn-secondary" onClick={() => setStep(4)}><ChevronLeft size={14} /> Back</button>
            <button className="btn btn-primary btn-lg" onClick={() => setStep(8)} disabled={!canNext6}>
              Continue <ChevronRight size={15} />
            </button>
          </div>
        </div>
      )}


      {/* ── STEP 7: Add-ons ──────────────────────────────── */}
      {step === 8 && (
        <div>
          <h2 className="gold-accent" style={{ marginBottom:6 }}>Add-ons</h2>
          <p style={{ color:'var(--text-secondary)', fontSize:13, marginBottom:20 }}>
            Optional extras on top of the package. Customer can add as many as they want.
          </p>

          {packages.map((pkg, pi) => {
            const pkgAddOns = pkg.addOns || [];
            const ADDON_OPTIONS = [
              { id:'drone', name:'Drone', defaultRate: 5000 },
              { id:'social_slides', name:'Social Media Slides (per set)', defaultRate: 200 },
              { id:'reel', name:'Instagram Reel', defaultRate: 1000 },
              { id:'teaser', name:'Wedding Teaser', defaultRate: 2000 },
              { id:'highlight', name:'Wedding Highlight', defaultRate: 5000 },
              { id:'invite', name:'Digital Wedding Invitation', defaultRate: 1000 },
              { id:'bts', name:'BTS Video (Gimbal)', defaultRate: 3000 },
              { id:'extra_sheets', name:'Extra Album Sheets (per sheet)', defaultRate: 80 },
              { id:'extra_album', name:'Extra Album Copy', defaultRate: 3000 },
              { id:'minibook', name:'Mini Book', defaultRate: 800 },
              { id:'calendar', name:'Calendar', defaultRate: 500 },
              { id:'pendrive', name:'Pendrive', defaultRate: 500 },
              { id:'keyring', name:'Key Ring (pair)', defaultRate: 200 },
              { id:'cutout', name:'Cutout', defaultRate: 500 },
            ];
            return (
              <div key={pi} className="card" style={{ marginBottom:14 }}>
                <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:14 }}>
                  <span className={`badge ${pkg.product.tier==='Best'?'badge-best':pkg.product.tier==='Luxe'?'badge-luxe':'badge-elite'}`}>{pkg.product.tier}</span>
                  <span style={{ fontSize:14, fontWeight:600 }}>{pkg.product.productName}</span>
                  {pkgAddOns.length > 0 && (
                    <span style={{ marginLeft:'auto', fontSize:13, fontWeight:700, color:'var(--gold)' }}>
                      +{formatCurrency(pkgAddOns.reduce((s:number,a:any)=>s+a.cost,0))}
                    </span>
                  )}
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:8 }}>
                  {ADDON_OPTIONS.map(opt => {
                    const existing = pkgAddOns.find((a:any)=>a.id===opt.id);
                    const isAdded = !!existing;
                    return (
                      <div key={opt.id} style={{ padding:'10px 12px', borderRadius:'var(--radius-sm)', border:`1.5px solid ${isAdded?'var(--gold)':'var(--border)'}`, background:isAdded?'var(--gold-bg)':'#fff', transition:'all 0.12s' }}>
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: isAdded?8:0 }}>
                          <div>
                            <div style={{ fontSize:13, fontWeight:isAdded?600:400, color:isAdded?'var(--gold)':'var(--text-primary)' }}>{opt.name}</div>
                            <div style={{ fontSize:11, color:'var(--text-muted)' }}>Default ₹{opt.defaultRate.toLocaleString('en-IN')}</div>
                          </div>
                          <button onClick={() => {
                            if (isAdded) {
                              updatePackage(pkg.id, { addOns: pkgAddOns.filter((a:any)=>a.id!==opt.id) });
                            } else {
                              updatePackage(pkg.id, { addOns: [...pkgAddOns, { id:opt.id, name:opt.name, cost:opt.defaultRate, qty:1 }] });
                            }
                          }}
                            style={{ padding:'4px 10px', borderRadius:100, fontSize:12, cursor:'pointer', fontFamily:'inherit', fontWeight:600, background:isAdded?'var(--gold)':'#F7FAFC', color:isAdded?'#fff':'var(--text-secondary)', border:`1px solid ${isAdded?'var(--gold)':'var(--border)'}` }}>
                            {isAdded ? '✓ Added' : '+ Add'}
                          </button>
                        </div>
                        {isAdded && (
                          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                            <div style={{ flex:1 }}>
                              <label style={{ fontSize:10 }}>Rate (₹)</label>
                              <input type="number" className="input" style={{ padding:'4px 8px', fontSize:12 }}
                                value={existing.cost}
                                onChange={e => updatePackage(pkg.id, { addOns: pkgAddOns.map((a:any)=>a.id===opt.id?{...a,cost:Number(e.target.value)}:a) })} />
                            </div>
                            <div style={{ width:60 }}>
                              <label style={{ fontSize:10 }}>Qty</label>
                              <input type="number" min={1} className="input" style={{ padding:'4px 8px', fontSize:12 }}
                                value={existing.qty||1}
                                onChange={e => updatePackage(pkg.id, { addOns: pkgAddOns.map((a:any)=>a.id===opt.id?{...a,qty:Number(e.target.value)}:a) })} />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          <div style={{ display:'flex', justifyContent:'space-between', marginTop:8 }}>
            <button className="btn btn-secondary" onClick={()=>setStep(7)}><ChevronLeft size={14}/> Back</button>
            <button className="btn btn-primary btn-lg" onClick={()=>setStep(9)}>
              Continue to Summary <ChevronRight size={15}/>
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 8: Summary + Markup ───────────────────────────── */}
      {step === 9 && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 300px', gap:20 }}>
          {/* Left: order summary */}
          <div>
            <h2 className="gold-accent" style={{ marginBottom:16 }}>Quote Summary</h2>

            {/* Customer */}
            <div className="card" style={{ marginBottom:12 }}>
              <div style={{ fontSize:11, fontWeight:600, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>Customer</div>
              <div style={{ fontWeight:700, fontSize:16 }}>{customerName}</div>
              <div style={{ color:'var(--text-secondary)', fontSize:13 }}>{customerPhone}{customerEmail && ` · ${customerEmail}`}</div>
              <div style={{ color:'var(--text-secondary)', fontSize:13, marginTop:4 }}>Event: <strong>{eventName}</strong></div>
            </div>

            {/* Packages */}
            {packages.map((pkg, i) => {
              const albumCost = pkg.album?.totalAlbumCost || 0;
              const liveDaysSum = Math.max(1, pkg.eventDates.filter(d => d.date).length);
              const liveContractorSum = (pkg.selectedDeliverables||[]).reduce((s,d) => s + (d.ratePerDay||d.rate) * liveDaysSum * (d.qty||1), 0) || pkg.contractorRate;
              const addOnSum = (pkg.addOns||[]).reduce((s,a) => s + (a.cost * (a.qty||1)), 0);
              const pkgTotal = liveContractorSum + albumCost + addOnSum;
              return (
                <div key={pkg.id} className="card" style={{ marginBottom:12 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12, paddingBottom:10, borderBottom:'1px solid var(--border)' }}>
                    <div>
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:2 }}>
                        <span style={{ fontWeight:700 }}>{pkg.product.serviceName}</span>
                        <span className={`badge ${pkg.product.tier==='Best'?'badge-best':pkg.product.tier==='Luxe'?'badge-luxe':'badge-elite'}`}>{pkg.product.tier}</span>
                      </div>
                      <div style={{ fontSize:13, color:'var(--text-secondary)' }}>{pkg.product.productName}</div>
                      <div style={{ fontSize:11, color:'var(--text-muted)', fontFamily:'monospace', marginTop:2 }}>{pkg.product.code}</div>
                    </div>
                    <span style={{ fontWeight:700, color:'var(--gold)', fontSize:16 }}>{formatCurrency(pkgTotal)}</span>
                  </div>

                  {pkg.eventDates.length > 0 && (
                    <div style={{ marginBottom:10 }}>
                      {pkg.eventDates.map((ed, j) => (
                        <div key={j} style={{ fontSize:12, color:'var(--text-secondary)' }}>
                          📅 {ed.date ? new Date(ed.date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : ed.date} — {ed.label}
                        </div>
                      ))}
                    </div>
                  )}

                  {(() => {
                    const liveDays = Math.max(1, pkg.eventDates.filter(d => d.date).length);
                    const liveRate = (pkg.selectedDeliverables||[]).reduce((s,d) => s + (d.ratePerDay||d.rate) * liveDays * (d.qty||1), 0) || pkg.contractorRate;
                    const anyQty = (pkg.selectedDeliverables||[]).some(d => (d.qty||1) > 1);
                    return (
                      <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, marginBottom:4 }}>
                        <span style={{ color:'var(--text-secondary)' }}>
                          Contractor: {pkg.contractor}
                          {liveDays > 1 && <span style={{ color:'var(--teal)', marginLeft:6 }}>× {liveDays} days</span>}
                          {anyQty && <span style={{ color:'var(--teal)', marginLeft:6 }}>(qty applied)</span>}
                        </span>
                        <span style={{ fontWeight:500 }}>{formatCurrency(liveRate)}</span>
                      </div>
                    );
                  })()}

                  {pkg.album && (
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, marginBottom:4 }}>
                      <span style={{ color:'var(--text-secondary)' }}>Album: {pkg.album.vendorName} · {pkg.product.numAlbums} album(s)</span>
                      <span style={{ fontWeight:500 }}>{formatCurrency(albumCost)}</span>
                    </div>
                  )}
                  {/* Album Adjustments in summary */}
                  {pkg.album&&(()=>{
                    const dr=albumDesignChargePerSheet[pkg.id]||0;
                    const bs=pkg.album!.sheets||40;
                    const es=extraAlbumSheets[pkg.id]||0;
                    const n=pkg.album!.numAlbums||1;
                    const sp=albumSplitSets[pkg.id]||1;
                    const cp=albumAdditionalCopies[pkg.id]||0;
                    const primaryDesign=dr>0?bs*dr*n:0;
                    const extraPaper=es*pkg.album!.paperRatePerSheet*n;
                    const extraDesign=dr>0?es*dr*n:0;
                    const splitCost=sp>1?(sp-1)*pkg.album!.coverRate*n:0;
                    const oneCopy=pkg.album!.totalAlbumCost+primaryDesign+extraPaper+extraDesign;
                    return <>
                      {dr>0&&<div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:4}}>
                        <span style={{color:'var(--text-secondary)'}}>+ Design charge: {bs} sheets × ₹{dr} × {n} album(s)</span>
                        <span style={{fontWeight:500,color:'var(--gold)'}}>+{formatCurrency(primaryDesign)}</span>
                      </div>}
                      {es>0&&<div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:4}}>
                        <span style={{color:'var(--text-secondary)'}}>+ Extra {es} sheets (paper{dr>0?' + design':''})</span>
                        <span style={{fontWeight:500,color:'var(--gold)'}}>+{formatCurrency(extraPaper+extraDesign)}</span>
                      </div>}
                      {sp>1&&<div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:4}}>
                        <span style={{color:'var(--text-secondary)'}}>+ Split: {sp} sets</span>
                        <span style={{fontWeight:500,color:'var(--gold)'}}>+{formatCurrency(splitCost)}</span>
                      </div>}
                      {cp>0&&<div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:4}}>
                        <span style={{color:'var(--text-secondary)'}}>+ {cp} additional copy(s) × {formatCurrency(oneCopy)}</span>
                        <span style={{fontWeight:500,color:'var(--gold)'}}>+{formatCurrency(cp*oneCopy)}</span>
                      </div>}
                    </>;
                  })()}
                  {pkg.album&&(()=>{
                    const pe=photoEditCharge[pkg.id];
                    if(!pe?.enabled||(pe.qty||0)===0) return null;
                    return <div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:4}}>
                      <span style={{color:'var(--text-secondary)'}}>📸 Photo Editing: {pe.qty} photos × ₹{pe.rate||50}/photo</span>
                      <span style={{fontWeight:500,color:'#0369A1'}}>+{formatCurrency((pe.rate||50)*(pe.qty||0))}</span>
                    </div>;
                  })()}
                  {/* Add-ons */}
                  {(pkg.addOns||[]).length > 0 && (
                    <div style={{ marginTop:4 }}>
                      {(pkg.addOns||[]).map((a:any, ai:number) => (
                        <div key={ai} style={{ display:'flex', justifyContent:'space-between', fontSize:13 }}>
                          <span style={{ color:'var(--text-secondary)' }}>+ {a.name}{a.qty > 1 ? ` × ${a.qty}` : ''}</span>
                          <span style={{ fontWeight:500, color:'var(--gold)' }}>+{formatCurrency(a.cost * (a.qty||1))}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {(() => {
                    const ec = extraCharges[pkg.id];
                    if (!ec || !ec.value || !pkg.album) return null;
                    const extraCost = ec.mode === 'sheets'
                      ? ec.value * pkg.album.paperRatePerSheet * (pkg.product.numAlbums || 1)
                      : ec.value;
                    return (
                      <div style={{ display:'flex', justifyContent:'space-between', fontSize:13 }}>
                        <span style={{ color:'var(--amber)' }}>Extra day charge ({ec.mode === 'sheets' ? `${ec.value} extra sheets` : 'flat charge'})</span>
                        <span style={{ fontWeight:500, color:'var(--amber)' }}>+{formatCurrency(extraCost)}</span>
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>

          {/* Right: markup + save */}
          <div>
            <div className="card" style={{ marginBottom:12 }}>
              <h4 style={{ marginBottom:12 }}>Markup</h4>
              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                {MARKUP_OPTIONS_LIST.map(opt => (
                  <button key={opt.value} onClick={() => setMarkupType(opt.value)}
                    style={{ padding:'10px 14px', borderRadius:'var(--radius-sm)', fontSize:14, fontWeight:600, cursor:'pointer', fontFamily:'inherit', transition:'all 0.12s', background: markupType===opt.value?'var(--gold)':'#fff', color: markupType===opt.value?'#fff':'var(--text-secondary)', border:`1.5px solid ${markupType===opt.value?'var(--gold)':'var(--border)'}`, textAlign:'left' }}>
                    {opt.label}
                  </button>
                ))}
                {user.canSeeNPNL && (
                  <button onClick={() => setMarkupType('npnl' as MarkupType)}
                    style={{ padding:'10px 14px', borderRadius:'var(--radius-sm)', fontSize:13, cursor:'pointer', fontFamily:'inherit', transition:'all 0.12s', background: markupType==='npnl'?'var(--red-bg)':'#fff', color: markupType==='npnl'?'var(--red)':'var(--text-muted)', border:`1.5px solid ${markupType==='npnl'?'var(--red)':'var(--border)'}`, textAlign:'left' }}>
                    NPNL — 0% (Admin only)
                  </button>
                )}
              </div>
            </div>

            <div className="card" style={{ marginBottom:16 }}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8, fontSize:13 }}>
                <span style={{ color:'var(--text-secondary)' }}>Base cost</span>
                <span>{formatCurrency(totalCost)}</span>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8, fontSize:13 }}>
                <span style={{ color:'var(--text-secondary)' }}>Markup ({markupPercent}%)</span>
                <span style={{ color:'var(--amber)' }}>{formatCurrency(markupAmount)}</span>
              </div>
              <div className="divider" />
              <div style={{ display:'flex', justifyContent:'space-between' }}>
                <span style={{ fontWeight:700, fontSize:16 }}>Quoted Amount</span>
                <span style={{ fontWeight:700, fontSize:22, color:'var(--gold)' }}>{formatCurrency(quotedAmount)}</span>
              </div>
              {user.canSeeInternalCost && (
                <div style={{ marginTop:10, padding:'8px 10px', background:'var(--bg-hover)', borderRadius:6 }}>
                  <div style={{ fontSize:11, color:'var(--text-muted)' }}>Internal cost: {formatCurrency(totalCost)}</div>
                  <div style={{ fontSize:11, color:'var(--teal)' }}>Profit: {formatCurrency(markupAmount)}</div>
                </div>
              )}
            </div>

            <button className="btn btn-primary btn-lg" style={{ width:'100%', justifyContent:'center', marginBottom:8 }} onClick={handleSave}>
              <FileText size={15} /> Save Quote
            </button>
            <button className="btn btn-secondary" style={{ width:'100%', justifyContent:'center' }} onClick={() => window.print()}>
              🖨 Print
            </button>

            <div style={{ marginTop:14 }}>
              <button className="btn btn-secondary" onClick={() => setStep(9)}><ChevronLeft size={14} /> Back</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}